/**
 * Every text of the web app (4b step 3b). English is the source; other languages give the same
 * shape (`Messages`). Entries with values are functions; sentences with emphasis are JSX, so a
 * translation can move the emphasis with the words. Words that name the three different locks:
 *   a capsule is "sealed" until its time (time lock),
 *   the key on this device is "locked" until the PIN opens it,
 *   the copy on our server is "blocked" after 10 wrong PINs.
 * Recovery-phrase words stay the English BIP-39 list in every language.
 */
import type { LegalCopy } from "../legal";

export const en = {
  pilotAdmin: {
    unassigned: 'Links without a recipient (no reserved place)',
    invalid: 'Invalid addresses (will not be saved)',
    duplicates: 'Repeated addresses',
    recover: "Recover an existing account",
    recoverGoogle: "Verify the new Google login",
    recoverHelp: "First verify your new sign-in address, then prove your existing identity with its recovery phrase. This uses your existing account, even when the pilot is full.",
  "save": "Save",
  "users": "Users",
  "settings": "Pilot settings",
  "places": "Occupied places",
  "remaining": "Remaining",
  "max": "Maximum pilot places",
  "inviteLimit": "Open invitations per member",
  "seatRule": "Predefined, active and suspended users occupy a place. Deactivated users release it.",
  "inviteRule": "The invitation limit is simultaneous open invitations, not a lifetime recruit limit. Zero stops new member invitations.",
  "ownerOnly": "Only the owner can change pilot limits and open registration.",
  "bulk": "Predefine users",
  "bulkHelp": "Paste up to 500 email addresses. Each new address reserves a place. No email is sent.",
  "preview": "Preview",
  "newUsers": "New reservations",
  "existing": "Already known",
  "saved": "Users saved and places reserved.",
  "reason": "Enter a reason (3 to 200 characters).",
  "confirmAction": "Apply to selected users",
  "activate": "Activate",
  "suspend": "Suspend",
  "deactivate": "Deactivate",
  "referral": "Referral (invitation)",
  "hasKey": "Has key",
  "no": "No",
  "all": "All",
  "byInvite": "By invitation",
  "withoutInvite": "Without invitation",
  "selected": "Selected",
  "select": "Select",
  "kind": "Account type",
  "human": "User",
  "services": "Service accounts",
  "role": "Role",
  "inviteHelp": "Address-bound invitations reserve a place immediately. An anonymous link reserves no place.",
  "revoked": "Revoked",
  "used": "Used",
  "expired": "Expired",
  "open": "Open",
  "mailed": "Mail sent",
  "notMailed": "Mail not sent",
  "resend": "Resend",
  "origins": {
    "manual": "Manually predefined",
    "bootstrap": "Owner bootstrap",
    "open": "Open registration",
    "invitation": "Invitation"
  }
},
  htmlLang: "en",
  /** Dates and numbers: British English, 24-hour clock, day before month. */
  locale: "en-GB",
  langName: "English",
  title: "Vrelis – time capsules",
  browse: {
    unknownCreated: "Creation time is not available on this device.",
    filter: "Filter", sort: "Sort by", sender: "Sender", recipient: "Recipient", subject: "Subject", group: "My group", allGroups: "All groups", personPlaceholder: "Name or nickname", created: "Created", received: "Received", opens: "Opening time", from: "From (≥)", to: "To (≤)", asc: "Ascending", desc: "Descending", reset: "Reset", timeNote: "Dates use your device's time zone. From/to include the selected minute. Received is when the server got the capsule; the time a private capsule was written is known only inside it.",
    showMore: "Show more", tooManyPeople: "More than 50 people match; only the first 50 are used. Type more of the name.", tooShort: "Type at least 2 letters of a name.", senderFilterOff: "Your lists show senders only after a click, so the Inbox has no sender or group filter.", senderUnchecked: (n: number) => `${n} more ${n === 1 ? "capsule" : "capsules"} from this filter not shown: the sender shows only after a click, is unverified, or needs your key.`, linked: "The linked capsule",
    archived: "Archived", archive: "Archive", restore: "Restore", following: "Following", watching: "Watching", blocked: "Blocked", search: "Search", searchPlaceholder: "Search people or capsules", users: "Users", messages: "Capsules", results: "Search results", searchNote: "Search looks at the subject, the intro of a public capsule, the author of a public capsule and the recipients in Sent. It does not search opened text or who sent you a private capsule. Archived capsules are excluded.", matches: (n: number) => `${n} results`, matchesMore: (n: number) => `${n}+ results`, loading: "Loading more capsules…", noResults: "Nothing found. Search looks at subjects, public intros, dates and names; it never sees the text of a sealed capsule.", openIn: (name: string) => `View in ${name}`, invalidRange: "The start must be before the end of each date range.",
  },
  design: {
    sortOpeningLatest: "Opening · latest first", sortOpeningEarliest: "Opening · earliest first", sortReceivedLatest: "Received · newest first", sortReceivedEarliest: "Received · oldest first",
    messageState: "Opening status", otherLists: "Other capsule lists", clearFilters: "Clear filters",
    budgetWindow: "last 24 h", budgetTotal: "total", budgetDay: "24 h",
    refresh: "Refresh", refreshing: "Refreshing…", messageBudget: (used: number, total: number) => `Deliveries used: ${used} of ${total}`, budgetNote: "Technical limit on deliveries in the last 24 hours. Your own copies do not count.", budgetNoteTotal: "Deliveries this service account may make in total. Copies do not count.",
    sendPrivate: "Send a private capsule", allMessages: (name: string) => `View all capsules from ${name}`, followToWrite: "Follow this person to send a private capsule.", followingTitle: "People", followingNote: "Who you follow, who follows you, and who you block or hide.", block: (name: string) => `Block ${name}`, unblock: (name: string) => `Unblock ${name}`, hide: (name: string) => `Hide ${name} in Feed`, unhide: (name: string) => `Show ${name} in Feed again`,
    groupsAndPeople: "Groups & people", editGroups: "Edit groups", openGroups: "Open my groups", hidePeople: "Hide groups and people", showPeople: "Show groups and people",
    viewPublicMessages: "View public capsules", viewMessages: "Public", message: "Capsule", writeTo: (name: string) => `New capsule to ${name}`, clearFilter: "Show everyone",
    openedAt: (time: string) => `Opened ${time}`, countdown: "Opens in", sealedInside: "The message is inside and still locked.", targetUnavailable: "This recipient is no longer available. Choose who can read your capsule.",
    navigation: "Navigation", profileSettings: "Profile & settings", options: "Additional options",
    export: "Download capsules", details: "Details", composeNote: "A capsule, a recipient, a moment in time.", memberHelp: "About member setup",
    subjectHint: "Visible before opening, to Vrelis too, along with the opening time. Keep private information in the text.",
    opensAt: (time: string) => `Opens ${time}`, timingDetails: "About the time lock",
    availableSince: (time: string) => `Available since ${time}`,
    memberSearch: "Search by name or email", membersTab: "Members", invitesTab: "Invites", settingsTab: "Settings",
    adminNote: "Manage members, invitations and access.", accountNote: "Your profile and preferences, in one place.",
    messageActions: "Capsule actions", untitled: "Untitled capsule", noMembers: "No matching members.",
  },

  common: {
    continue: "Continue",
    back: "Back",
    cancel: "Cancel",
    finish: "Finish",
    open: "Open",
    opening: "Opening…",
    download: "Download",
    downloading: "Downloading…",
    copy: "copy",
    remove: "remove",
    loading: "Loading…",
    loadingSlow: "This is taking longer than usual. Check your connection.",
    tryAgain: "Try again",
    pin: "PIN",
    repeatPin: "Repeat PIN",
    recoveryPhrase: "Recovery phrase",
    waitingPasskey: "Waiting for the passkey…",
    noWindowHint:
      "No passkey window? It may be behind this window or waiting in the taskbar; your browser or password manager may also need an update. Then try again.",
    forgotPin: "Forgot the PIN? Use your recovery phrase.",
    you: "you",
    unknown: "unknown",
    minutes: (n: number) => `${n} min`,
    more: "More",
  },

  time: {
    units: { y: "y", d: "d", h: "h", min: "min", s: "s" },
  },

  /** Pilot stage: the header badge and the open call on the landing page. */
  /** Try it: one guided first capsule (reviews/06-action-plan.md section 4). */
  tryIt: {
    title: "Try it",
    lead: (minutes: number) => `A guided first capsule, not how Write normally works: write a few words to yourself, seal them for about ${minutes} minutes, and watch the lock hold and then open. A real capsule can stay sealed for years.`,
    subjectSuggestion: "My first capsule",
    textLabel: "Your text",
    textPlaceholder: "A few words for yourself in ten minutes.",
    toYou: (minutes: number) => `To you. It opens in about ${minutes} minutes; until then nobody can read it, not even you or Vrelis.`,
    seal: "Seal it",
    skip: "Skip, write a real capsule",
    sealedTitle: "Sealed.",
    opensIn: (left: string, at: string) => `It opens in ${left}, at ${at}. Stay on this page, or come back later: it is also in Sent.`,
    readyTitle: "Its time has come.",
    open: "Open it",
    openedTitle: "Opened.",
    openedNote: "This is what every capsule does, at the time you choose: nobody can read it before, and after it only the people it was sealed to.",
    writeReal: "Write a real capsule",
    again: "Try again",
    phraseTitle: "Keep your phrase",
    phraseText: "Your 12 words are your key. Whoever has them is you; without them and without this device, your private capsules cannot be opened. Keep them on paper, away from the computer.",
    copyTitle: "Download a copy",
    copyText: "The copy and the offline opener let you open this capsule with your phrase even if vrelis.com is gone. The opener asks the public drand relays for the signal of its time.",
    download: "Download this capsule",
    opener: "Download the offline opener",
    accountLink: "Try it: a guided first capsule",
    accountNote: " Seal a few words to yourself for ten minutes and see a capsule open.",
  },

  pilot: {
    badge: "Pilot stage",
    badgeTitle: "Vrelis is in its pilot stage: we are testing it with a small group of people.",
    noInvite: "No invitation? Join the pilot",
    title: "Join the pilot",
    intro: "Vrelis is in its pilot stage. We are testing it with a small group of people before opening it to everyone, and anyone who wants to help test it is welcome.",
    how: "To join, write to us and include:",
    fields: ["your email address (we send the invitation there; for Google sign-in, use your Google account's address)", "your location: city and country", "your occupation and company, if you wish"],
    use: "We use these details only to invite you to the pilot and to contact you about it.",
    backupNote: "Keep your recovery phrase and download the capsules that matter to you.",
    button: "Apply by email",
    subject: "Vrelis pilot",
    body: "Hello,\n\nI would like to join the Vrelis pilot.\n\nEmail:\nLocation (city, country):\nOccupation (optional):\nCompany (optional):\n",
  },

  footer: {
    pricing: "Plans",
    developers: "Developers",
    theme: "Theme:",
    themes: { system: "System", light: "Light", dark: "Dark" },
    language: "Language:",
    translationNote: "Some pages are still in English while translations are being completed.",
    report: "Report a problem",
    guide: "How Vrelis works",
    terms: "Terms of use",
    privacy: "Privacy",
    cookies: "Cookies",
    contact: "About and contact",
    reportSubject: "Vrelis: a problem",
    operator: "Perikle PR, Belgrade, Serbia",
    aiAssisted: "AI-assisted",
    aiAssistedTitle: "Vrelis is designed and built with the help of AI tools.",
  },

  legal: {
    updated: "Version 1.1 · 23 October 2026 · Free pilot",
    navigation: "Legal information",
    summaryTitle: "In short",
    contact: "For support, questions about your data and reports of abuse, write to",
    loginNote: "Use of the platform is covered by our",
    loginPrivacy: "How we handle your data:",
    freePilot: "Vrelis is currently a free pilot run by Perikle PR of Belgrade, Serbia. Selling is not switched on yet: nothing is charged and nothing renews by itself.",
    sellingLive: "Vrelis is run by Perikle PR of Belgrade, Serbia. Paid plans are sold through our payment provider; prices and what they include are on Plans.",
    pages: {
      terms: {
        title: "Terms of use",
        lead: "What Vrelis offers during the free pilot, what we are responsible for, what you are responsible for, and where the limits of storing and opening time-locked capsules lie.",
        summary: [
          "Vrelis is a free pilot run by Perikle PR of Belgrade, Serbia. There is nothing to pay and no automatic charge.",
          "Your capsule is encrypted in your browser. Our server holds no readable key that opens it.",
          "Nobody can open a capsule before its time: not us, not the recipient, not any authority.",
          "We cannot recover a lost key. Keep your recovery phrase and download what matters to you.",
          "Do not use Vrelis for unlawful content, harassment or spam. We may restrict accounts that do.",
        ],
        sections: [
          { heading: "1. Who runs Vrelis and what these terms cover", paragraphs: [
            "Vrelis at vrelis.com is run by Perikle PR of Belgrade, Serbia. For questions, support and complaints, write to the address at the bottom of this page. These terms cover the website, your account, the API and the offline opener you can download.",
            "We are building Vrelis as a commercial software product. For now the operator pays for it from its own resources. The pilot is free: there are no subscriptions, purchases or automatic renewals. If we ever introduce a paid service, you will see the offer and its terms before you decide to buy. Using the pilot does not mean you agree to any future charge.",
            "How we handle data is described separately on the Privacy and Cookies pages. Using Vrelis is not general consent to processing that is not necessary. Your rights under the law stay intact.",
          ] },
          { heading: "2. Capsules and the time lock", paragraphs: [
            "Vrelis lets you send encrypted messages, which we call capsules, with attachments and an opening time you choose. After that time, a public capsule can be read by anyone with access to the Feed. A private capsule also needs the recipient's key. The guide and the app itself describe what is currently possible and the limits.",
            "Nobody can open a capsule before its time: not us, not the recipient, not any authority, for any reason. Our server holds no readable key that could open it. The time lock does not depend on our goodwill, but on published cryptography and the drand network. We also cannot recall a capsule that someone has already downloaded.",
            "When the software works as intended, content is encrypted in your browser before it is sent. The time lock waits for a signal that the drand network publishes at a precise moment. The time shown with a capsule is the earliest moment it can be opened. It is not a promise that an email will arrive then, or that the recipient will read it then.",
            "Security depends on the cryptography, the drand network, the software we deliver to you, your browser and device, and how you look after your keys. We do not promise absolute security, or that Vrelis will work on every future device and format. A signature shows which key signed a capsule, but on its own it does not prove who the person behind that key is.",
          ] },
          { heading: "3. Keys, drand and your own copies", paragraphs: [
            "Keep your recovery phrase and your own copy of important capsules and files. Recovering your account or setting a new password does not bring back a lost encryption key. A private capsule sent to your earlier key may still need that earlier key. If you lose all your keys and every way of recovering them, the content may stay out of reach for good. Support has no spare key to open it either.",
            "If drand never publishes the signal a capsule is waiting for, the capsule may stay locked forever. Downloading the encrypted capsule in advance does not remove that risk. If the signal has been published but is missing from your archive, you will need to obtain it. The fact that the opening date has passed does not, on its own, mean your copy can be opened offline.",
            "A capsule can be opened without the internet if a complete archive contains the verified drand signal and all the files it needs, and, for a private capsule, you also have the right key. An exported opened text or file is an ordinary readable copy, without the protection of encryption. We cannot recall copies that someone else has already downloaded.",
          ] },
          { heading: "4. Availability and the end of the pilot", paragraphs: [
            "The pilot has no agreed service level and no promise of permanent storage. Features and limits may change, and maintenance, faults or outages at outside services may interrupt access. Do not use Vrelis as your only archive.",
            "We may pause or end the pilot. If we plan a change that seriously affects access or stored content, we will try to tell you in advance and leave you time to export, whenever that is reasonably possible. In an emergency that may not be possible. We answer for our own failures and unlawful acts as the law requires.",
            "Vrelis is not a qualified trust service, a legally valid delivery service, a notarial deposit, an executor of wills or an emergency system. Do not rely on it alone for a legal deadline, health, safety or significant assets.",
          ] },
          { heading: "5. Your account and acceptable use", paragraphs: [
            "Give accurate details and look after your sign-in methods and devices. Act for another person or organisation only if they have authorised you. If you suspect someone has taken over your account, tell us straight away. Never send support your password, PIN, recovery phrase or private key.",
            "Do not use Vrelis for unlawful content, exploitation of children, threats, harassment, fraud, infringing other people's rights, malicious software, unauthorised access or spam. Do not get around limits, quotas or protective measures, and do not disrupt the service. Send capsules and invitations only to people you have a genuine reason to contact.",
            "We may restrict an account, or remove content from our servers, when that is reasonably necessary because of abuse, a security risk or a legal obligation. Where appropriate and allowed, we will explain why, and you can ask us to review the decision at the address below. We cannot read private encrypted capsules, so we rely on reports, public content and the metadata we have.",
          ] },
          { heading: "6. Your content and other people", paragraphs: [
            "Your content remains yours. You allow us only the technical processing, storage and transfer needed for the features you use. This does not transfer ownership to us, and does not let us use private content for advertising or for training AI models.",
            "You are responsible for having the right to send or publish your content, and for choosing recipients and opening times. Public subjects, public intros and profile details are not protected like the content of a private capsule. Even the discreet display does not hide every detail of who sends to whom. Respect other people's privacy and their copyright and other intellectual property rights.",
          ] },
          { heading: "7. Responsibility and rights that cannot be excluded", paragraphs: [
            "We offer the free pilot with the features and technical limits described on this page. We do not guarantee uninterrupted service, that a recipient will manage to read a capsule, that every capsule can be recovered, or that the service will run forever. Whether and how far we are responsible for a loss depends on the applicable law, the obligation involved, the cause and the circumstances.",
            "Nothing in these terms excludes or limits our responsibility for intentional wrongdoing, gross negligence, or anything else the law does not allow to be excluded or limited. That holds even though the service is free. Pointing to drand or other suppliers does not automatically release us from our own legal obligations.",
          ] },
          { heading: "8. Questions, governing law and changes", paragraphs: [
            "These terms are governed by the law of the Republic of Serbia. If you are a consumer, you also keep the mandatory protection given to you by the law of the country where you live. Nothing here asks you to give up going to a competent court or supervisory authority.",
            "Write to us if something does not work, a capsule has gone missing, you have a privacy question, or you disagree with a measure taken against your account. Include the capsule or account reference, the approximate time and a short description. Do not send private keys or private content that is not needed. We handle requests as the law requires.",
            "We publish a version date with these terms and update them when the service changes. We will tell you about important changes in a suitable way. Where the law requires you to accept the terms again or give consent, changing this page does not replace that. Future paid terms will not apply retroactively to free use.",
          ] },
        ],
      },
      privacy: {
        title: "Privacy notice",
        lead: "How Perikle PR of Belgrade, Serbia, handles your personal data while you use the free Vrelis pilot. Encryption protects the content of capsules, but it does not make all account and usage data anonymous.",
        summary: [
          "We keep your name, email, sign-in methods, public key, encrypted capsules and the metadata needed to deliver them.",
          "We cannot read private capsules. The server does see who sent a capsule to whom, when and how large it is.",
          "Cloudflare hosts the service, Resend sends the mail and Google handles Google sign-in.",
          "We do not sell your data and do not use your capsules for advertising or AI training.",
          "You can ask for a copy, correction or deletion of your data at the address below, even without an account.",
        ],
        sections: [
          { heading: "1. Who handles your data", paragraphs: [
            "The controller for Vrelis is Perikle PR of Belgrade, Serbia. Send requests about your data to the address below, even if you have no account. This notice covers accounts, capsules, attachments, the API and support. During the pilot we collect no payment card details and there is no payment on the site.",
          ] },
          { heading: "2. What data we have and where it comes from", paragraphs: [
            "We get data from you, from your use of the service, from the service you sign in with, and from other users who send you a capsule or an invitation. Account data means your email, the name you display, profile details you enter, internal identifiers, public keys and sign-in methods. The owner and administrators see account data, including email addresses, when that is needed to run the service.",
            "For password sign-in we do not keep your password, only a value derived from it that lets us check it is correct. For passkeys we keep the data needed to verify a sign-in; the fingerprint or face scan that unlocks your device never reaches Vrelis. At sign-in Google gives us only the details you approve, such as an identifier, your name and email. We get no access to your Gmail or Google Drive.",
            "Service data means encrypted capsules and files, public keys and signatures, sending records, recipient details, public capsule metadata, your profile, contacts, follows, blocks, archives and other settings. Subjects and intros of public capsules are visible before opening. Subjects and intros of private capsules are inside the encrypted part. The server still sees who a capsule is sent to and when.",
            "Technical data means IP address, browser and request details, times, errors, usage counters and security events. A support request contains your contact details and the text and files you send. Do not put passwords, your recovery phrase, private keys or sensitive data that is not needed into a request.",
          ] },
          { heading: "3. Encryption and data on your device", paragraphs: [
            "When the software works as intended, private content is encrypted on your device, and the server keeps only the encrypted form and the metadata needed to run. The copy of your key that we keep on the server, if you choose it, is also encrypted. This does not mean every piece of data is secret, or that the operator never sees any personal data.",
            "Your device may keep a copy of your key, opened capsules and settings. Opened public capsules may be stored in readable form. Opened private capsules are stored encrypted with a key derived from your private key. On a device that does not support the required protection, the key copy may be stored without the extra protection of a passkey and PIN; the app warns you about this during setup. The Cookies page explains what is kept in your browser and how to delete it.",
          ] },
          { heading: "4. Why we process data and on what basis", paragraphs: [
            "We use data to open your account and sign you in, provide the features you ask for, store and deliver capsules, apply limits and answer support requests. When you use the service and the processing is necessary for it, the legal basis is performing our agreement with you, or steps you ask for before it is made.",
            "Security, preventing abuse, reliable operation, limited performance measurement and protection in a possible legal dispute can rest on legitimate interests. In those cases we weigh whether the processing is really needed and whether your rights outweigh it. Where the law requires, we process data to meet a legal obligation. Processing that legally needs consent gets a separate consent request; accepting the Terms does not replace it.",
            "If you are a recipient or invitee without an agreement with us, the sender's agreement is not an agreement with you. We process the data needed to deliver the capsule or invitation and to prevent abuse on a separate basis, usually legitimate interests where that is allowed. An invitation is not permission to send you advertising.",
          ] },
          { heading: "5. Suppliers and who else receives data", paragraphs: [
            "Cloudflare delivers the website and API, stores the database and files, protects the service and provides Web Analytics. Resend sends the service's emails. Google handles sign-in when you choose it. Each supplier processes data in its own role and under its own terms; not all of them have the same legal role.",
            "Cloudflare Web Analytics measures visits and site speed. According to Cloudflare, it does not use cookies, local storage or fingerprinting (recognising a device by its characteristics) to identify visitors, and does not track individuals across its customers' sites. Your browser still sends requests and technical measurements to Cloudflare, so this does not mean there is no processing at all.",
            "Recipients and other users see what the visibility and sharing you choose allow them to see. A drand relay (a server that passes on the drand signal) sees your IP address and request details when your device fetches a signal, but not the text of your capsule. We may give data to an authority when the law requires it, or when necessary and lawful to protect someone's rights. We cannot give anyone readable content we do not have.",
            "We do not sell your data for other companies' advertising and do not use private capsules for advertising or AI training. Our suppliers' policies:",
          ], links: [
            { label: "Cloudflare privacy policy", href: "https://www.cloudflare.com/privacypolicy/" },
            { label: "Cloudflare Web Analytics", href: "https://developers.cloudflare.com/web-analytics/about/" },
            { label: "Resend privacy policy", href: "https://resend.com/legal/privacy-policy" },
            { label: "Google privacy policy", href: "https://policies.google.com/privacy" },
          ] },
          { heading: "6. Processing outside Serbia", paragraphs: [
            "The operator is in Serbia, but our suppliers run global infrastructure, so data may be processed or accessed in other countries as well. We do not claim that all data stays in Serbia or the EU. Transfers to other countries must comply with data protection law. You can ask at the address below which recipients, countries and safeguards are involved.",
          ] },
          { heading: "7. How long we keep data", paragraphs: [
            "We keep account and capsule data while it is needed to run the pilot, respecting your rights, our legal obligations and the rules on ending the pilot. There is no fixed period after which every account or capsule is deleted automatically. Hiding or archiving a capsule is not deleting it.",
            "How long we keep a record depends on its purpose: running the service, account security, an open support request, preventing abuse, a legal obligation or the period for a possible legal dispute. When the purpose ends, we delete or anonymise the data, unless the law or other users' rights require otherwise. If you want us to delete your data, tell us; deactivating an account does not by itself delete every record.",
            "The service regularly and automatically deletes expired email tokens and old temporary security records and email-sending counters. Hosting logs, supplier copies and backups have their own periods. We have no control over files that you or a recipient have downloaded; deleting an account does not delete them. The Cookies page explains how to delete data from your browser.",
          ] },
          { heading: "8. Invitations and people without an account", paragraphs: [
            "A user can enter your email to invite you or send you a capsule. We then keep the address, the status of the invitation or delivery and the necessary security records. We received that information from the user who contacted you. You can write to us without registering: to ask what data we hold, to ask not to receive further invitations, or to request deletion.",
          ] },
          { heading: "9. Your rights, complaints and security", paragraphs: [
            "Under the conditions set by law, you can ask to see your data and get a copy, and ask for correction, deletion, restriction or transfer of your data. You can object to processing based on legitimate interests and withdraw consent if you gave it. We may check your identity as far as needed, but never by asking for your private key or recovery phrase. We reply within the legal deadline, which under Serbian law is generally 30 days; if we need to extend it, we will tell you.",
            "You can complain to Serbia's Commissioner for Information of Public Importance and Personal Data Protection, or to another competent authority. You do not have to contact us first. We protect data with measures suited to the risk and report incidents as the law requires. Encryption does not mean an incident is impossible.",
            "Automatic quota and security checks protect the service. If a check restricts you by mistake, ask us to review it. We do not use private capsules to build advertising profiles. The pilot is not meant for children and does not ask for sensitive data; if you notice a problem with an account or data, tell us.",
          ], links: [{ label: "Commissioner of the Republic of Serbia", href: "https://www.poverenik.rs" }] },
          { heading: "10. Changes", paragraphs: [
            "We update this notice when our processing or the law changes, and tell you about important changes in a suitable way. Changing this text does not replace fresh consent where it is needed. If we introduce payments, we will describe them here before they start.",
          ] },
        ],
      },
      cookies: {
        title: "Cookies and browser data",
        lead: "Vrelis uses a few cookies and browser records for sign-in, settings and encrypted capsules. Local Storage and IndexedDB are places where your browser keeps data, but they are not cookies.",
        summary: [
          "One cookie keeps you signed in for up to 7 days; another protects Google sign-in for 10 minutes.",
          "Your browser keeps your language, theme, key and opened capsules.",
          "Cloudflare Web Analytics counts visits without cookies. There are no advertising pixels.",
          "Before you clear browser data, make sure you have your recovery phrase.",
        ],
        sections: [
          { heading: "1. Sign-in cookies", paragraphs: [
            "tc_session keeps you signed in for up to seven days from sign-in, unless the session ends or is cancelled earlier. On the site it is protected by the Secure, HttpOnly and SameSite=Lax settings. It contains no capsule content and not your private key.",
            "tc_oauth protects Google sign-in and lasts at most ten minutes. Both cookies are used only for sign-in and security. If you block them, sign-in will not work. We treat cookies that are necessary for a service you ask for separately from optional ones that need consent.",
          ] },
          { heading: "2. Settings and data on your device", paragraphs: [
            "In Local Storage, the vrelis-lang and vrelis-theme records remember your language and theme until you change or delete them. The timecapsule.keyStepSkipped record remembers, separately for each member, that the key setup step was skipped in this browser. These records only serve the app and are not used for advertising.",
            "The IndexedDB database timecapsule, store kv, holds the local copy of your key, opened capsules and file details, sending notes and public keys seen before. Opened private capsules are encrypted; public ones may be readable. On a device whose passkey does not support PRF (an extension that allows the extra encryption), the key copy may lack that extra layer of protection. So protect access to your device.",
          ] },
          { heading: "3. Analytics and outside services", paragraphs: [
            "The site uses Cloudflare Web Analytics to measure visits and speed. Cloudflare states that Web Analytics does not use cookies, local storage or fingerprinting to identify visitors. The measurement requests and other processing for hosting and security are described on the Privacy page.",
            "Google sign-in opens Google's own service, which may use its own cookies under its own rules. The pilot has no payment on the site and no advertising pixels. We do not switch on optional technologies that need consent without your consent. If we introduce any, we will first update this page and, where needed, give you a choice.",
          ] },
          { heading: "4. How to change or delete data", paragraphs: [
            "You change the theme and language in the app, and you can view and delete cookies and site data in your browser settings. The Forget this device button deletes from your browser what Vrelis has stored for the current account. It does not delete the account on the server, other accounts' data or archives you have downloaded.",
            "Before you delete browser data, check that you can get your key back another way. If you lose the last usable copy of your key, private capsules may stay out of reach for good. Signing out, locking the key and deleting browser data are not the same thing. The guide explains recovery and export.",
          ] },
        ],
      },
      contact: {
        title: "About Vrelis and contact",
        lead: "Vrelis is software for encrypted messages that open only at a time you choose; we call these messages capsules. The free pilot is run by Perikle PR of Belgrade, Serbia.",
        summary: [
          "Vrelis is a free pilot run by Perikle PR of Belgrade, Serbia, and paid for by the operator from its own resources.",
          "One address takes support questions, data requests and abuse reports. You do not need an account.",
          "Never send us your password, PIN, recovery phrase or private key.",
        ],
        sections: [
          { heading: "1. Who is behind Vrelis", paragraphs: [
            "Operator: Perikle PR, Belgrade, Republic of Serbia. Website: vrelis.com. Email: support@vrelis.com; the Report a problem link in the site footer leads to the same address.",
            "For now the operator pays for development and servers from its own resources. Vrelis is a commercial software project with a free pilot: there are no purchases, subscriptions or automatic charges. The guide explains what Vrelis can do today; the Terms of use describe availability and technical limits.",
          ] },
          { heading: "2. Support and reporting problems", paragraphs: [
            "Send a short description, the capsule reference if you have one, the approximate time and which browser or device you use. Do not send your password, PIN, recovery phrase or private key. Support is not available around the clock and we have no agreed response time. Vrelis is not an emergency service.",
          ] },
          { heading: "3. Privacy and abuse", paragraphs: [
            "Send questions about personal data, unwanted invitations, unlawful content and account measures to the same address. You do not need an account. Write enough for us to find the problem, but do not forward private or unlawful content unless it is necessary. The Privacy page explains your rights; the Terms of use explain the rules and how to ask for a review of an account measure.",
          ] },
          { heading: "4. Keep your own copy", paragraphs: [
            "Keep your recovery phrase and export the content you want to keep. Vrelis does not promise to store anything forever. An exported capsule still opens only with its files, the right drand signal and, for a private capsule, your key. The Terms of use and the guide give the details.",
          ] },
        ],
      },
    },
  } satisfies LegalCopy,

  app: {
    cannotReach: (detail: string) => `Cannot reach the server: ${detail}`,
    nav: { feed: "Feed", inbox: "Inbox", sent: "Sent", watching: "Watching", correspondence: "Correspondence", write: "New capsule", admin: "Admin" },
    keyMismatch: "The key on this device is not your account's current identity. ",
    noKey: "You have no key yet: you can read public capsules, but not write or receive private ones. ",
    noKeyLink: "Create your key under Keys",
    keyMissing: "Your key is not on this device, so you cannot write or open private capsules here. ",
    setUpLink: "Set it up with your passkey or recovery phrase",
  },

  login: {
    lead: "Write a message today that nobody can read before the date you choose. Not the recipient, not us, not you.",
    heroTitle: "Messages sealed by time",
    story: {
      eyebrow: "On secrets and time",
      title: "Even secrets have their time.",
      myth: "King Midas hid his donkey ears. His barber whispered the secret into the earth, but reeds grew from that very spot. Whenever the wind blew, the reeds would tell it.",
      reflection: "Perhaps this story stays with us because we know the fine line between the need to keep something close and the wish to share it. Some words wait for courage. Some for the right person. Some simply for their time.",
      closing: "A time capsule holds that moment for you. You choose when a secret can become a story.",
    },
    features: {
      label: "What can you do with Vrelis?",
      carousel: "carousel",
      choose: "Choose a feature",
      previous: "Previous feature",
      next: "Next feature",
      more: "How it works and who it is for",
      forWhom: "For whom?",
      example: "Try this:",
      sealedReply: {
        motto: "Write your view. Seal your answer. Open it when the time comes.",
        explanation: "Like an answer in a sealed envelope: everyone records their own view before reading anyone else’s. The replies open for the person who asked the question; they choose whether to share them afterwards.",
        uses: [
          { label: "Independent views", text: "First, everyone’s own answer. Then, a shared discussion." },
          { label: "Estimates and offers", text: "Record it today. Compare at the agreed time." },
          { label: "Predictions", text: "Predict now. Let the future tell." },
        ],
      },
      slides: {
        sase: {
          eyebrow: "Ask for a reply",
          title: "Answer now. Discover later.",
          description: "Ask several people the same question. Each answers privately, and nobody, not even you, can read the answers before the date you chose.",
          how: "Write a private capsule and tick \"Ask for a reply\". You pay each recipient's first reply in advance, one stamp per person, so answering costs them nothing. The replies go only to you and stay sealed until the opening time you set, or one each recipient chooses; recipients do not see each other's answers. An ordinary reply can be read the moment it arrives; this one cannot, so no answer can follow an earlier one. The guide calls this SASE, after the stamped, self-addressed envelope.",
          audience: "Friends collecting predictions, families making memories, or teams gathering independent ideas before reading the answers.",
          example: "Five friends, one question in January: \"Where will we be next New Year's Eve?\" Each answers alone; on 31 December you open all five answers together, and nobody could peek before.",
        },
        future: {
          eyebrow: "Messages to the future",
          title: "Some words need the right moment.",
          description: "A letter to your future self, a birthday surprise, or a memory for someone dear. Write today and choose when it can be opened.",
          how: "Choose a recipient and an opening date. The message and its attachments are encrypted in your browser. A public time beacon, drand, makes opening possible only when that moment arrives — even Vrelis cannot open it sooner.",
          audience: "Anyone who wants to save a thought, mark a milestone, or send a surprise that keeps its secret.",
          example: "Write yourself a letter today and open it on your next birthday.",
        },
        group: {
          eyebrow: "Groups & CC",
          title: "One moment. Your favourite people.",
          description: "Send a sealed message to several people at once. Choose a private group send, or CC when everyone should see who else received it.",
          how: "Choose people individually or from your address-book groups. Each recipient gets a private capsule. With CC, the recipient list is visible inside the sealed message; without CC, recipients do not see who else received it. You can add a SASE reply envelope too.",
          audience: "Families, friends, and teams sharing a memory, a plan, or a question with the same opening date.",
          example: "Send a reunion memory to your friends, sealed until your next get-together.",
        },
        public: {
          eyebrow: "Private or public",
          title: "For someone special. Or everyone.",
          description: "Keep a capsule between you and its recipient, or make it public for everyone in Vrelis. Either way, time keeps the contents sealed.",
          how: "Private capsules are encrypted for the chosen recipients. Public capsules can be read by everyone in the pilot once their time arrives. Both can include attachments. A public capsule can also have a short introduction visible before opening.",
          audience: "People saving personal memories, and authors who want to share a prediction, a story, or an idea with the community later.",
          example: "Publish a prediction today. Let everyone read it when the year ends.",
        },
      },
    },
    heroPoints: [
      "The time lock comes from a public randomness beacon (drand): the key to read a capsule exists only from the moment you chose.",
      "Public capsules for everyone in the pilot; private ones for the people you follow, or for someone who is not a member yet.",
      "Your key is made and kept in your browser. Our server stores only sealed capsules and has no key to open them.",
    ],
    trustLink: "How the lock can be trusted",
    // The three steps on the landing page (PLAN.md 4.33).
    steps: {
      title: "How it works",
      items: [
        "Write a letter, a note or a memory, and add files if you like.",
        "Choose who can read it and when it opens: in five minutes or in twenty years.",
        "It is sealed in your browser before it leaves. Until that moment nobody can read it: not the recipient, not Vrelis, not you.",
      ],
    },
    signInTitle: "Sign in",
    invite: "You have an invite. Sign in with the Google account it was sent to, or register below with that address.",
    google: "Continue with Google",
    privacy: "Google only tells us your name and email address. Your key is made and kept in your browser; Vrelis never sees it.",
    guide: "How Vrelis works",
    errors: {
      invite_required: "This pilot requires an approved email address or a valid invitation. Use the approved address to register, or open your invitation link.",
      invite_invalid: "This invite is not valid: it was already used, has expired, or is for a different email address.",
      member_suspended: "Your account is suspended. Contact the administrator at support@vrelis.com.",
    status_conflict: "The user changed meanwhile. Reload and try again.",
    owner_only: "Only the owner can change these settings.",
    target_self: "You cannot block your own account.",
    pilot_full: "All pilot places are occupied. Contact the administrator.",
    member_deactivated: "Your access to this pilot has been ended by the admin. If you think this is a mistake, contact the admin.",
      state_mismatch: "The login took too long or was started in another tab. Please try again.",
      google: "Google did not confirm the login. Please try again.",
      other: (code: string) => `Login failed (${code}).`,
    },
    passkey: {
      button: "Sign in with a passkey",
      busy: "Waiting for the passkey…",
    },
    password: {
      modes: { signin: "Email and password", register: "Register" },
      email: "Email",
      name: "Name (optional)",
      password: "Password",
      newPassword: "New password",
      repeat: "Repeat the password",
      rule: "At least 10 characters.",
      differ: "The two passwords differ.",
      busy: "Working…",
      submit: { signin: "Sign in", register: "Register", reset: "Send a reset link" },
      forgot: "Forgot the password?",
      registerNote: "We send a link to this address; the account exists after you open it. The password only signs you in: your key stays behind your passkey and PIN.",
      checkMail: (email: string) => `We sent a link to ${email}. Open it within one hour to finish.`,
      resetText: "Enter the address of your password. If it has one, we send a link to set a new password.",
      resetSent: "If this address has a password, a link is on its way. It is valid for one hour.",
    },
  },

  verify: {
    title: { recover: "Confirm the new sign-in address", register: "Confirm your address", add: "Confirm the new password", reset: "Set a new password" },
    text: {
      register: (email: string) => `Confirm ${email} to create your account and sign in.`,
      recover: (email: string) => `Confirm ${email}, then prove your existing identity with its recovery phrase.`,
      add: (email: string) => `Confirm ${email} to add password sign-in to your account.`,
      reset: (email: string) => `Choose a new password for ${email}. Every other session of the account ends.`,
    },
    confirm: { recover: "Confirm and continue recovery", register: "Confirm and sign in", add: "Confirm", reset: "Save and sign in" },
    home: "Back to Vrelis",
  },
  /** /unsubscribe?t=: the link at the end of every notification mail (PLAN.md 4.28). */
  unsubscribe: {
    title: "Stop these mails",
    question: (kind: string) => `Stop mails of this kind: ${kind}?`,
    confirm: "Stop them",
    done: (kind: string) => `Done: no more mails of this kind: ${kind}. You can switch it on again in Account.`,
    settings: "Notification settings in Account",
  },

  onboarding: {
    welcome: (name: string) => `Welcome, ${name}`,
    needKey: "Before you write anything, you need your personal key.",
    googleRole: (
      <>
        <b>Signing in</b> (Google, password or passkey) only opens your account.
      </>
    ),
    keyRole: (
      <>
        <b>Your key</b> is your identity: it signs your capsules and opens private capsules sent to you. It is created in this browser.
      </>
    ),
    phraseRole: (
      <>
        <b>Your recovery phrase</b> is 12 words that rebuild the key on any device, also in 20 years and also if this website no longer exists. You write it
        on paper; we never see it.
      </>
    ),
    createKey: "Create my key",
    havePhrase: "I already have a recovery phrase",
    phraseTitle: "Your recovery phrase",
    typePhrase: "Type the 12 words in order. They stay in this browser.",
    phraseIs: (npub: string) => `This phrase is the identity ${npub}.`,
    relinkText: (owner: string) => (
      <>
        This identity belongs to the member <b>{owner}</b>. If that is you and you can no longer sign in the old way, move it to this login. The member keeps this
        sign-in method; an old one of the same kind (Google or password) stops working, other ones stay.
      </>
    ),
    relink: "Move my identity to this login",
    relinking: "Moving…",
    writeDown: "Write these 12 words on paper, in this order, and keep the paper somewhere safe. Do not photograph them or store them on a computer.",
    whoeverHas: (
      <>
        Whoever has these words <b>is</b> you: they can sign as you and open your private capsules. If you lose them and every device with your key, your private
        capsules can never be opened. We cannot recover them for you.
      </>
    ),
    wrote: "I wrote the 12 words on paper.",
    checkTitle: "Check your paper",
    checkText: "Type these words from your paper.",
    word: (n: number) => `Word ${n}`,
    showAgain: "Show the words again",
  },

  keySetup: {
    pinRule: (min: number) => `Digits only, at least ${min}.`,
    pinsDiffer: "The two PINs differ.",
    wordCount: (n: number) => `${n} of 12 words.`,
    unknownWord: (n: number, word: string) => `Word ${n}, "${word}", is not a recovery word. Check the spelling.`,
    checksum: "All words are known, but they do not form a valid phrase. Check the order.",
    title: "Protect the key on this device",
    saving: "Saving your key…",
    switchNeedsKey: "To switch to this identity you need your current key on this device. If you lost it, ask the admin to reset your identity.",
    switchPurpose: "to confirm the switch with your current key",
    passkeyName: (name: string) => `Vrelis – ${name}`,
    noPrf: "The passkey was created, but this browser or device cannot derive keys from it (no PRF support).",
    switchText: (next: string, current: string) =>
      `This makes ${next} your identity instead of ${current}. Capsules signed with the current one stay attributed to it.`,
    switchPrivate:
      "Private capsules sent to your current identity stay sealed to it: they will not open under the new one. Keep the current recovery phrase to open them later. Members who write to you will see that your key changed.",
    understand: "I understand",
    choose: (serverCopy: boolean) =>
      `Choose a PIN and create a passkey (fingerprint, face, or your device PIN). Together they lock the key on this device${serverCopy ? " and the copy we keep for moving to a new device" : ""}. We never see the PIN or the key.`,
    pinProtects: (serverCopy: boolean): string =>
      serverCopy
        ? "The PIN protects our copy against guessing: after 10 wrong PINs it is blocked, and only your phrase unblocks it. On this device it only stops a casual thief: someone who can also pass your passkey check could try every PIN."
        : "The PIN protects the key on this device. It only stops a casual thief: someone who can also pass your passkey check could try every PIN.",
    createPasskey: "Create passkey and finish",
    withoutPasskeyText: (
      <>
        You can continue without a passkey. The key is then stored <b>unencrypted</b> in this browser: anyone who can use this browser profile can use your
        identity. We keep <b>no copy</b> on our server: to use another device, type your recovery phrase there.
      </>
    ),
    withoutPasskey: "Continue without passkey",
  },

  restore: {
    welcomeBack: (name: string) => `Welcome back, ${name}`,
    relinked: (npub: string) => `This Google login now opens your identity ${npub}. Protect the key on this device.`,
    title: "Set up your key on this device",
    identity: (npub: string) => `Your identity: ${npub}`,
    blocked: "The copy on our server is blocked after 10 wrong PINs. Use your recovery phrase; it writes a new copy and unblocks it.",
    noPasskey: "No passkey on this device, or forgot the PIN?",
    usePhrase: "Use your recovery phrase",
    usePasskeyInstead: "Use your passkey and PIN instead",
    passkeyText: "Your passkey (synced from your other device, or on your phone) and your PIN open the copy of your key that we keep. We cannot open it ourselves.",
    attemptsLeft: (n: number) => `${n} PIN attempts left before the copy is blocked.`,
    usePasskey: "Use passkey and PIN",
    blockedShort: "The server copy is blocked. Use your recovery phrase.",
    phraseText: "Type the 12 words of your recovery phrase. They rebuild your key here in the browser and are never sent to us.",
    otherMember: (name: string) => `This phrase is the identity of another member (${name}). It cannot be used with this account.`,
    otherIdentity: (npub: string) =>
      `This phrase is a different identity (${npub}) than the one your account uses now. Switching needs your current key on this device; if you lost it, ask the admin to reset your identity.`,
    useThis: "Use this identity",
  },

  keyBar: {
    unlockedUntil: (time: string | null) => `Your key is unlocked in this browser${time ? ` until ${time}` : ""}.`,
    lockNow: "Lock now",
    locked: "Your key is locked. Vrelis asks for your PIN when you open or send something private. To read several in a row, unlock it for a while:",
    unlockFor: (minutes: number) => `Unlock for ${minutes} minutes`,
    lock: "Hide opened",
    lockTitle: "Hides every private capsule you opened on this page",
    wrongPin: "Wrong PIN.",
  },

  keyDialog: {
    title: "Your PIN",
    text: (purpose: string) => `Vrelis needs your key ${purpose}. Enter your PIN, then confirm with your passkey. The key opens only in this browser.`,
    keep: "Keep the key unlocked for",
    minutes: "minutes",
    purposes: {
      open: "to open this capsule",
      send: "to sign and send this capsule",
      profile: "to sign your profile",
      download: "to download this capsule",
      downloadMany: "to download private capsules",
      reset: "to confirm the identity reset",
      role: "to confirm the role change",
    },
  },

  // The confirmation after sending (PLAN.md 4.31).
  sealedNotice: {
    title: "Your capsule is sealed and saved.",
    opens: (time: string) => `It opens ${time}. Until then nobody can read what is inside, not even you.`,
    everyone: "Then everyone in the pilot can read it.",
    self: "Then only you can read it.",
    people: (n: number) => `Recipients: ${n}.`,
    copyNote: "Keep a copy of your own: it opens with the offline opener even without vrelis.com.",
    download: "Download a copy",
    filesNote: "To keep a copy with its files, download the capsule from the list below.",
    close: "Close",
  },

  compose: {
    // The summary before sealing (PLAN.md 4.31).
    summaryTitle: "Before you seal",
    summaryTo: "Who can read it",
    summaryOpens: "Opens",
    summaryReplies: "Replies open",
    summaryVisiblePublic: "Everyone sees before the opening",
    summaryVisiblePrivate: "The recipients and Vrelis see before the opening",
    summarySealed: "Sealed until the opening",
    summaryText: (chars: string) => `Text: ${chars} characters`,
    summaryFiles: (n: string) => `Files: ${n}`,
    summaryCost: "Cost",
    summaryFree: "No stamps",
    summaryEveryone: "Everyone in the pilot: a public capsule",
    summarySelf: "Only you",
    summaryNewcomer: (email: string) => `${email}, not a member yet`,
    summaryMore: (n: number) => `+${n}`,
    // Unsent drafts kept in the tab's memory (PLAN.md 4.29).
    draftRestored: "Your unsent draft is back.",
    draftDiscard: "Discard draft",
    draftDiscardConfirm: "Discard this draft? Its text and files cannot be brought back.",
    draftKept: "Your draft stays while this tab is open, even if you go to another page. Closing or reloading the tab loses it.",
    // Reply envelope, CC and replies (PLAN.md 4.21).
    envelope: "SASE · reply envelope",
    envelopeAsk: "Collect sealed answers",
    envelopeIntro: "Send a question and collect views, assessments or predictions. Each answer stays private and sealed until its opening time. Only you receive the answers; they are not published automatically.",
    envelopeDetails: "How the reply envelope works",
    envelopeNote: "Each recipient can send you one private reply, sealed to your key, even if they do not follow you. You pay for the capsule and for the reply now (2 stamps per recipient); a second reply from the same person costs them a stamp. Everything stays on your Correspondence page, apart from the rest.",
    envelopeFixed: "Replies open at a time I set",
    envelopeFree: "Replies open at a time the recipient chooses",
    envelopeOpens: (time: string) => `Replies open ${time}.`,
    envelopeDeadline: "After that time no reply is accepted.",
    envelopeCost: (n: number) => (n === 1 ? "With the envelope this uses 1 stamp." : `With the envelope this uses ${n} stamps.`),
    cc: "CC",
    ccNote: (max: number) => `Up to ${max} more members you follow get the same capsule, and everyone listed sees who else received it (the names are sealed inside; our server never sees the list). Each gets a capsule of their own.`,
    replyTitle: "Reply",
    replyNote: "A private reply to a request with a reply envelope.",
    replyTo: (name: string) => `To ${name}, who asked for this reply:`,
    replyOnlyToSender: "Your reply goes only to them, sealed to their key. Nobody in CC sees it.",
    replyTimeFixed: (time: string) => `The sender set the opening time: ${time}. Your reply opens then.`,
    replyTimeFree: "The sender left the opening time to you.",
    replyExpired: "The time the sender set has passed; no reply is accepted any more.",
    replyFirstCost: () => "The sender's stamp pays for this reply. Your own copy, if you keep one, is free.",
    replyLaterCost: () => "You already replied once; this reply uses one stamp. The earlier reply stays, and the sender sees both.",
    replyUnavailable: "The member who asked for a reply has no key any more, so no reply can be sealed to them.",
    ccLeftOut: "The list sealed inside the capsule still names them as CC, because it was signed before the send: tell the others, if it matters.",
    newcomer: "By email",
    discreet: "Discreet",
    discreetNote: "The recipient's lists show \"Discreet sender\" instead of your name until they click the capsule. Only the recipient can see that it is you; the text still waits for its time.",
    blockedOne: (name: string) => `${name} does not accept your private capsules.`,
    blockedMany: (names: string) => `These members do not accept your private capsules; they will not get this one: ${names}.`,
    groupAllBlocked: "Nobody in this group accepts your private capsules.",
    leftOut: (names: string) => `Sent to the group, except: ${names}. They do not accept your private capsules.`,
    toSent: "Go to Sent",
    newcomerNote: "We mail them an invite. When they join and ask for the capsule, you confirm it is them and it is sealed to their key; until then only your copy exists.",
    severalMembers: (n: number) => `Group${n ? ` (${n})` : ""}`,
    severalNote: (max: number) => `2 to ${max} members you follow. Each gets a capsule of their own; nobody sees who else got it.`,
    loadGroups: "Add from my groups (asks for the PIN)",
    manyKeysChanged: (names: string) => `These keys changed since this browser last saw them: ${names}. Check their fingerprints with them before you send.`,
    manyKeysChecked: "I checked the new keys",
    groupCost: (n: number) => (n === 1 ? "Uses 1 stamp, one per recipient. Your copy is free." : `Uses ${n} stamps, one per recipient. Your copy is free.`),
    budget: (limit: number, total: boolean) => (total ? `${limit} capsules in total` : `${limit} daily capsules`),
    subject: "Subject",
    subjectNote: "Required. The subject is never encrypted: whoever can see the capsule reads it at once, also before it unlocks, and so does our server.",
    intro: "Short description (optional)",
    introHint: "Read before the opening: by everyone for a public capsule, only by the recipient for a private one. Lists show it right after the subject.",
    introCount: (used: string, max: string) => `${used} / ${max} characters`,
    visibleBefore: "Visible before opening",
    sealedTitle: "Inside the capsule",
    sealedNote: "The text and files are encrypted in your browser. Nobody, Vrelis included, can read them before the opening time.",
    sealedUntil: (time: string) => `Locked until ${time}`,
    addFiles: "Add files",
    presets: { m5: "5 minutes", h1: "1 hour", d1: "1 day", w1: "1 week", y1: "1 year" },
    needKey: "Set up your key on this device to write capsules.",
    noKeyYet: "You need a key to write capsules.",
    title: "Write a capsule",
    message: "Text",
    bytes: (used: string, max: string) => `${used} / ${max} bytes`,
    files: "Files",
    madeSmaller: " · made smaller, metadata removed",
    preparing: "Preparing… ",
    filesNote: (used: string, max: string, count: number) =>
      `${used} of ${max}, up to ${count} files. Photos are made smaller and lose their metadata, including location. Files are encrypted here; their keys travel inside the time lock.`,
    tooManyFiles: (n: number) => `At most ${n} files per capsule.`,
    opensIn: "Opens in",
    pickDate: "Pick a date",
    opensAt: (time: string, round: string) => (
      <>
        Opens at <b>{time}</b> (drand round {round}, ±3 s). Nobody can read it before then, including you.
      </>
    ),
    tooLate: "Pick a time before 1 January 2126.",
    inPast: "Pick a time in the future.",
    whoCanRead: "Who can read it",
    everyone: "Everyone",
    oneMember: (name: string | null) => name ?? "One person",
    search: "Search members",
    followFirst: "Private capsules go only to members you follow. Follow someone first (Account, or \"Follow\" on their capsule).",
    youMark: " (you)",
    keyOf: (name: string) => `Key of ${name}: `,
    since: (time: string) => ` · since ${time}`,
    fullKey: (name: string) => `Full key, to compare with ${name}`,
    keyChanged: (name: string, when: string | null, was: string, now: string) => (
      <>
        {name}&apos;s key changed{when ? ` on ${when}` : ""} (it was <code>{was}</code>). Check with them outside this app that <code>{now}</code> is really
        their new key before you send anything private. Sending waits until you confirm.{" "}
      </>
    ),
    checked: (name: string) => `I checked the new key with ${name}`,
    toSelf: "A capsule to yourself is sealed to your own key, so you open it after the time like any recipient. No extra copy is needed.",
    keepCopy: "Keep a free copy for me",
    copyOffConfirm: "Send without a copy? Then you will not be able to read this capsule afterwards, not even after it opens. Only the recipient can.",
    uses: (n: number) => (n === 1 ? "Uses 1 stamp." : `Uses ${n} stamps.`),
    usesBudget: (n: number, budget: string) => `Counts as ${n} of your ${budget}.`,
    stampStatus: (cost: number, left: number) => `${cost === 1 ? "1 stamp" : `${cost} stamps`} for this capsule · you have ${left}.`,
    stampRule: "One stamp is one delivery to one person. Your own copy is free.",
    stampShort: (cost: number, left: number) => `This needs ${cost} ${cost === 1 ? "stamp" : "stamps"} and you have ${left}.`,
    seeBilling: "Plans and stamps",
    moreSpace: "Need room for bigger files? See plans.",
    copyOn: (name: string) => `A free copy is sealed to your key, so you can read this capsule in Sent after it opens, like ${name}.`,
    copyOff: (name: string) => `Without a copy only ${name} can ever open this capsule. You will not be able to read it yourself, not even after it opens. Sent shows only when it opens.`,
    sealing: "Sealing…",
    sendAgain: "Send again",
    seal: "Seal and send",
    notDelivered: (detail: string) => `Nothing was delivered: ${detail}`,
    unconfirmed: 'We could not confirm delivery (connection problem). "Send again" resends the same capsule; it is never stored twice.',
  },

  capsules: {
    toMany: (names: string) => `to: ${names}`,
    unlockedTab: "Unlocked",
    lockedTab: "Still locked",
    titles: {
      feed: ["Feed", "Public capsules from everyone in the pilot."],
      inbox: ["Inbox", "Private capsules addressed to you."],
      sent: ["Sent", "Capsules you wrote."],
      watching: ["Watching", "Capsules you are waiting for, in opening order."],
      envelopes_sent: ["Correspondence, asked by me", "Requests with a reply envelope that you sent."],
      envelopes_received: ["Correspondence, asked of me", "Requests with a reply envelope sent to you."],
    } as Record<"feed" | "inbox" | "sent" | "watching" | "envelopes_sent" | "envelopes_received", [string, string]>,
    everyone: "Everyone",
    following: (n: number) => `People I follow (${n})`,
    followNobody: 'You follow nobody yet. Use "Follow" on a capsule to see that author\'s capsules here.',
    empty: "Nothing here yet.",
    emptyFiltered: "No capsule matches these filters.",
    emptyWrite: "Write a capsule",
    nextOpens: (time: string) => `The next capsule opens ${time}.`,
    newGroup: "New",
    openedGroup: "Opened",
    newLabel: "New",
    /** What each empty list is for (reviews/06-action-plan.md 3.8): never only "nothing here". */
    emptyStates: {
      feed: {
        unlocked: ["No opened public capsules yet.", "Public capsules from everyone in the pilot appear here once their opening time has passed. From then on anyone in the pilot can read them.", true],
        locked: ["No public capsules are waiting.", "Sealed public capsules wait here with their opening date and a countdown. Their subject, and an intro if the author wrote one, can be read now; the text only at the opening time.", true],
        watching: ["You watch no capsules.", "Use \"Watch\" on a sealed public capsule to keep it here until it opens.", false],
        following: ["Nothing from the people you follow.", "Public capsules of the people you follow appear here. Follow someone from their name on a capsule or from search.", false],
        archived: ["Nothing archived.", "Capsules you archive leave Feed, Inbox and Sent and wait here. Archiving hides a capsule only for you; restore it any time.", false],
      },
      inbox: {
        unlocked: ["No opened private capsules yet.", "Private capsules sent to you appear here once their opening time has passed. Only your key opens them.", false],
        locked: ["No private capsules are waiting for you.", "Private capsules addressed to you wait here, sealed, until their opening time. A member who follows you can send you one.", false],
        archived: ["Nothing archived.", "Capsules you archive leave Feed, Inbox and Sent and wait here. Archiving hides a capsule only for you; restore it any time.", false],
        blocked: ["Nothing from blocked members.", "Private capsules from members you block are kept here, apart from your Inbox.", false],
      },
      sent: {
        unlocked: ["None of your capsules has opened yet.", "Capsules you sent appear here once their opening time has passed. You can read a private one only if you kept your own copy.", true],
        locked: ["You have no sealed capsules.", "Capsules you send wait here with their opening date until they open: to yourself, to a member, or to an email address that is not a member yet.", true],
        archived: ["Nothing archived.", "Capsules you archive leave Feed, Inbox and Sent and wait here. Archiving hides a capsule only for you; restore it any time.", false],
      },
    } as Record<"feed" | "inbox" | "sent", Partial<Record<"unlocked" | "locked" | "watching" | "following" | "archived" | "blocked", [string, string, boolean]>>>,
    selectAll: "Select all shown",
    downloadSelected: (n: number) => `Download selected (${n})`,
    includeOpened: "Include opened text and files (not encrypted on your disk)",
    preparing: (n: number) => `Preparing ${n} capsule(s)…`,
    saved: (n: number, skipped: number) => `Saved ${n} capsule(s)${skipped ? `; ${skipped} skipped (only their recipient can read them)` : ""}.`,
    loadOlder: "Load older",
    damaged: (reason: string) => `Damaged capsule: ${reason}`,
    fromUnknown: "from unknown",
    fromYou: "from you",
    from: (name: string) => `from ${name}`,
    now: (name: string) => ` · now ${name}`,
    noName: " · no name in the capsule",
    toEveryone: "to everyone",
    to: (name: string) => `to ${name}`,
    privateText: "Private. Open it with your PIN to see who sent it and when it opens; the text once that time has come.",
    opensIn: (d: string) => `Opens in ${d}`,
    private: "private",
    discreetSender: "Discreet sender",
    unverifiedSender: "Unverified sender",
    unverifiedNote: "The key that signed this capsule is not the key of the account that sent it. Do not trust the name it carries.",
    intro: "Short description",
    selectForDownload: "Select for download",
    written: (time: string) => `written ${time}`,
    opensAt: (time: string) => ` · opens ${time}`,
    opensWhenOpened: " · opening time shows when you open it",
    opensRecipientOnly: " · opening time known only to the recipient",
    downloadZip: "The capsule with its files as a .zip, with the offline opener",
    downloadJson: "The capsule as a .json file",
    watch: "Watch",
    stopWatching: "Stop watching",
    follow: (name: string) => `Follow ${name}`,
    unfollow: (name: string) => `Unfollow ${name}`,
    keyMissing: "Your key is not on this device. Set it up to see this capsule.",
    noCopy: "Only the recipient can open this. You did not keep a copy.",
    oldKeyMissing: "Sent to an earlier key of yours that is not in your keyring. Add its phrase under Account, Keys.",
    suspect: "revoked key",
    suspectNote: "The sender later revoked the key that signed this, as stolen. It may not be from them.",
  },

  /** Correspondence (PLAN.md 4.21): requests with a reply envelope and their replies. */
  envelopes: {
    // The pointer at the top of Inbox (PLAN.md 4.32).
    inboxNote: "Requests for a reply, and the replies to your own requests, are kept in Correspondence.",
    inboxUnopened: (n: string) => `Requests for a reply you have not opened yet: ${n}. They are kept in Correspondence.`,
    inboxLink: "Open Correspondence",
    title: "Correspondence",
    subtitle: "Capsules with a reply envelope and the replies to them, apart from the rest.",
    tabs: { sent: "Asked by me", received: "Asked of me", archived: "Archived" } as Record<"sent" | "received" | "archived", string>,
    empty: "No correspondence yet.",
    emptyNote: "Write a private capsule and tick \"Ask for a reply\": it and its replies land here.",
    emptyNotes: {
      sent: "A request you send with \"Ask for a reply\" waits here with every reply to it. Each recipient answers privately, and the replies stay sealed until the time you chose, also for you.",
      received: "When someone asks you for a reply, the request lands here. Your reply goes only to them, sealed until their time; the first one is already paid by the person who asked.",
      archived: "Requests you archive wait here. Archiving hides them only for you; restore them any time.",
    } as Record<"sent" | "received" | "archived", string>,
    asksReply: "asks for a reply",
    asksReplyNote: "The sender asks you for a private reply back. It goes only to them.",
    replyOpensAt: (time: string) => `the reply opens ${time}`,
    replyOpensFree: "the reply opens at a time of the recipient's choice",
    termsSent: (when: string, paid: number) => `You asked for a reply; ${when}. Paid: ${paid}.`,
    termsReceived: (when: string) => `The sender asks for a reply; ${when}. Your reply goes only to the sender.`,
    expired: "The time has passed; no reply is accepted any more.",
    replied: (n: number, total: number) => `replied ${n} of ${total}`,
    noReply: "no reply yet",
    replies: (n: number) => `${n} ${n === 1 ? "reply" : "replies"}`,
    replyIndex: (n: number) => `reply ${n}`,
    stamp: "stamp",
    stampNote: "Who paid for this reply: the sender's stamp (the first reply), or the one who replied.",
    paidBy: (name: string) => `${name} paid`,
    youPaid: "you paid",
    matchesTime: "opens at the time you set",
    mismatchTime: "does not match your envelope",
    reply: "Reply",
    replyAgain: "Reply again",
    onlyToSender: "Your reply goes only to the sender.",
    myReplies: "My replies",
    moreReplies: "More than 100 replies; the rest are not listed.",
    senderUnknown: "The sender of this request is not known yet. Open it first.",
    toLabel: "To:",
    ccLabel: "CC:",
  },

  /** Role badges (PLAN.md 4.22, RoleBadge.tsx). */
  roles: {
    owner: "Owner",
    ownerHint: "Runs Vrelis and is the only one who makes or removes admins.",
    admin: "Admin",
    adminHint: "Helps run Vrelis: invites, members, registration.",
    service: "API account",
    serviceHint: "A service that reads and sends through the Vrelis API with tokens.",
  },

  account: {
    status: {
      none: "Your key is not on this device.",
      mismatch: "This device holds a different identity than your account's current one.",
      locked: "On this device, locked by passkey + PIN; the PIN is asked for when you open or send something private.",
      unlocked: "On this device, unlocked.",
    },
    yourKey: "Your key",
    manageKeys: "Manage keys: details, reset, keyring",
    preferences: "Preferences",
    privacy: "Privacy",
    hideSenders: "Show senders in my lists only after I click a capsule",
    hideSendersNote: "For people looking over your shoulder: every private capsule shows \"Discreet sender\" until you click it, on every device.",
    // Capsule notifications by mail (PLAN.md 4.28).
    notifications: {
      title: "Notifications by mail",
      note: "Mails go to your account's address. Each one has a link that switches its kind off.",
      kinds: {
        new_private: "A new private capsule arrives for me",
        new_public: "Once a day: new public capsules of people I follow",
        upcoming: "Capsules open soon: private ones for me, public ones of people I follow or watch",
        reply_due: "The time to answer a reply envelope runs out",
        unlocked: "A private capsule for me can be opened",
      },
      lead: "How long before",
      attach: "Attach the capsule as a file for the offline opener",
      attachNote: "The file stays locked: it opens with your recovery phrase once its time has come. A capsule's files are not attached.",
      utc: "Times in the mails are in UTC.",
    },
    preferencesNote: "Kept in this browser only.",
    identity: "Identity: ",
    plain: " Stored without passkey protection in this browser.",
    noServerCopy: "No copy on our server.",
    serverBlocked: "The copy on our server is blocked after 10 wrong PINs.",
    serverCopy: (left: number) => `Copy on our server: locked by passkey + PIN, ${left} PIN attempts left.`,
    setUpAgain: "Set up this device again with your recovery phrase",
    setUpAgainNote: " (new PIN and passkey; replaces the server copy).",
    notSetUp: "Not set up yet.",
    signIns: {
      title: "How you sign in",
      note: "Each of these opens your account. None of them opens your key: that still needs your passkey and PIN, or your recovery phrase.",
      kind: { google: "Google", password: "Password", passkey: "Passkey" },
      since: (day: string) => `(since ${day})`,
      remove: "remove",
      confirmRemove: (kind: string) => `Remove this sign-in method (${kind})?`,
      noPasskey: "No passkey signs in yet. A passkey you create when you set up your key on a device also signs you in.",
      addPassword: (email: string) => `Add a password for ${email}. We send a link to that address; the password works after you open it.`,
      addPasswordButton: "Add password",
    },
    following: "People",
    peopleLink: "Following, followers, blocked and hidden",
    peopleNote: " are on the People page.",
    followNobody: 'Nobody yet. Use "Follow" on a public capsule; people you invite and the one who invited you are followed for you. Only people you follow can get your private capsules.',
    unfollow: "unfollow",
    followSource: { invited: "you invited them", inviter: "invited you" },
    contactsLink: "Address book: groups and aliases",
    contactsNote: " (encrypted with your key; we cannot read it).",
    keepTitle: "Keep your capsules without us",
    opener: "Download the offline opener",
    openerNote: ": one HTML file that opens downloaded capsules on your computer, with your recovery phrase for private ones. It never contacts vrelis.com.",
    deviceTitle: "This device and your sessions",
    actions: (
      <>
        <li>
          <b>Lock</b>: removes the key from memory now; the PIN is asked again.
        </li>
        <li>
          <b>Log out</b>: locks and ends this session. The key and opened capsules stay on this device.
        </li>
        <li>
          <b>Log out everywhere</b>: ends your sessions on every device.
        </li>
        <li>
          <b>Forget this device</b>: logs out and deletes your key and everything Vrelis stored for you in this browser.
        </li>
      </>
    ),
    lock: "Lock",
    logout: "Log out",
    logoutEverywhere: "Log out everywhere",
    forget: "Forget this device",
    confirmEverywhere: "Log out on every device and browser where you are logged in, including this one?",
    confirmForget:
      "Forget this device?\n\nThis browser deletes your key, the text of capsules you opened, notes about capsules you sent and the keys you checked, then logs you out. Capsules on the server are not affected.\n\nTo use your identity here again you need your passkey and PIN, or your 12-word recovery phrase. Do you have it?",
    profileTitle: "Your profile",
    profileNote: (fp: string) => (
      <>
        Every capsule you send carries these fields, signed, as they are at that moment. Never your email or your Google account. Recipients see them with your
        key fingerprint <code>{fp}</code>.
      </>
    ),
    displayName: "Display name",
    nickname: "Nickname (optional)",
    about: (n: number) => `About (optional, up to ${n} characters)`,
    saveProfile: "Save profile",
    saved: "Saved. New capsules carry this name; capsules already sent keep theirs.",
  },

  guide: {
    back: "Back to Vrelis",
    contents: "Contents",
  },

  invites: {
    title: "Invite someone",
    text: "We mail an invite to this address. When they join, you follow each other.",
    limit: (n: number) => `At most ${n} open invitations.`,
    send: "Send invite",
    mailed: (email: string) => `The invite was mailed to ${email}.`,
    resent: (email: string) => `${email} already had your invite; it was mailed again with a new expiry date.`,
    notMailed: (why: string) => `The mail was not sent: ${why}`,
    orCopy: "You can also pass on the link yourself:",
    open: "waiting",
    used: "accepted",
  },

  billing: {
    title: "Billing",
    pageNote: "Your plan, your stamps and your storage.",
    previewNote: "Payments go through our payment provider, inside this page. What you already sent stays readable whatever happens to your plan.",
    seePlans: "Plans and stamps",
    planNames: { free: "Free", legacy: "Legacy", start: "Start" } as Record<string, string>,
    planTitle: "Your plan",
    planLine: (name: string, monthly: number) => `${name} · ${monthly} stamps a month`,
    attachmentLine: (size: string) => `Up to ${size} of files in one capsule`,
    activatedUntil: (date: string) => `Start activated until ${date}`,
    noAutoRenew: "It does not renew by itself.",
    renewsOn: (date: string) => `Renews automatically on ${date}.`,
    endsOn: (date: string) => `Cancelled: it ends on ${date} and does not renew.`,
    renewalUnknown: "Renewal: see Invoices and subscription.",
    refreshOn: (date: string) => `Monthly stamps refresh on ${date} (UTC).`,
    rightsTitle: "What your plan is made of",
    rightsNote: "Several rights can apply at once; you get the largest stamps and storage, and extra storage adds up.",
    rightSource: { launch: "Launch member", manual: "Given by an admin", subscription: "Start", pack_storage: "Pack storage", pack_attachment: "Pack files" } as Record<string, string>,
    rightLine: (what: string, from: string, until: string | null) => (until ? `${what} · ${from} to ${until}` : `${what} · from ${from}, no end date`),
    rightStamps: (n: number) => `${n} stamps a month`,
    rightStorage: (size: string) => `${size} of storage`,
    rightExtraStorage: (size: string) => `+${size} of storage`,
    rightFiles: (size: string) => `${size} of files per capsule`,
    stampsTitle: "Stamps",
    stampsLeft: (n: number) => (n === 1 ? "1 stamp left" : `${n} stamps left`),
    stampExplain: "One stamp pays one delivery to one recipient. Your own copy is free. Capsules you already sent stay readable whatever happens to your plan or your stamps.",
    upgrade: "Upgrade",
    buyPack: "Buy",
    checkoutTitle: "Payment",
    checkoutClose: "Close",
    checkoutTest: "Test mode: use the provider's test cards. No real money moves.",
    checkoutFake: "The test provider has no payment form. Tests complete this purchase on their own.",
    checkoutConfirming: "Paid. Waiting for the payment provider to confirm it; your plan or stamps appear here in a moment.",
    checkoutActivating: "Paid. The provider confirmed the payment; your plan or stamps are being activated.",
    checkoutSlow: "The confirmation is taking longer than usual. You can leave this page: the purchase is kept, and your plan or stamps appear when the provider confirms it.",
    checkoutError: "The payment form could not be opened or reported an error. Nothing was charged by Vrelis; try again.",
    checkoutNotReady: "payments are not fully set up yet",
    checkoutDone: "Thank you. Your purchase is active.",
    portal: "Invoices and subscription",
    portalNote: "Opens the payment provider's page in a new tab: invoices, payment method, changing or cancelling the subscription.",
    monthMeter: (used: number, granted: number) => `This month: ${used} of ${granted} used`,
    monthMeterName: "Stamps used this month",
    held: (n: number) => `${n} of these stamps are held for capsules waiting for new members. They come back if the capsule is declined or expires.`,
    expiring: "Stamps that expire within 30 days",
    expiringItem: (units: number, date: string) => `${units} on ${date}`,
    expiringMore: "and more later",
    storageTitle: "Storage",
    storageLine: (used: string, quota: string) => `${used} of ${quota} used`,
    storageMeterName: "Storage used",
    storageNote: "Storage counts the encrypted files you uploaded. Files in capsules you sent stay available to you and to the people you sent them to, whatever your plan.",
    overQuota: "You are over your storage. Everything you sent stays as it is, and you can still send text, but you cannot add new files until you free space or get more storage.",
    chooseTitle: "Choose a plan or stamps",
    carouselPrev: "Previous plans",
    carouselNext: "More plans",
    perMonth: "month",
    perYear: "year",
    every: (unit: "month" | "year", n: number) => (n > 1 ? `every ${n} ${unit}s` : `/ ${unit}`),
    planDetail: (monthly: number, storage: string, files: string) => `${monthly} stamps a month, ${storage} of storage, ${files} of files per capsule.`,
    free: { name: "Free", text: "Applies when nothing else does." },
    legacy: { name: "Legacy", text: "Given to the first members at launch." },
    startNotNeeded: "Start would add nothing to what you have, so it is not offered.",
    current: "Your plan",
    historyTitle: "History",
    historyNote: "Stamps used, stamps added, and your purchases, newest first.",
    tabs: { uses: "Stamps used", grants: "Stamps added", orders: "Purchases" } as Record<string, string>,
    nothingYet: "Nothing yet. Stamps you spend, stamps you are given and purchases are listed here as they happen.",
    packDetail: (stamps: number, stampMonths: number, storage: string, files: string, fileMonths: number) => [stamps > 0 ? `${stamps} stamps for ${stampMonths} months` : null, storage !== "0 KiB" ? `${storage} more storage for good` : null, files !== "0 KiB" ? `${files} of files per capsule for ${fileMonths} months` : null].filter(Boolean).join(", ") + ".",
    nothingOnSale: "Nothing is on sale yet.",
    notSelling: "Selling is not switched on yet. The plans below show how they will work; nothing can be bought and nothing is charged.",
    useSend: (units: number, messages: number) => `Sent ${messages} ${messages === 1 ? "capsule" : "capsules"} · ${units} ${units === 1 ? "stamp" : "stamps"}`,
    useSendFree: (messages: number) => `Sent ${messages} ${messages === 1 ? "capsule" : "capsules"} · no stamps`,
    useReserve: (units: number) => `Held ${units} ${units === 1 ? "stamp" : "stamps"} for a capsule waiting for a new member`,
    useRefund: (units: number) => `Returned ${units} ${units === 1 ? "stamp" : "stamps"} from a closed waiting capsule`,
    pendingStatus: { waiting: "waiting", requested: "asked for", delivered: "delivered", declined: "declined", expired: "expired" } as Record<string, string>,
    grantReasons: { period: "Monthly stamps", pack: "Stamp pack", manual: "Added by an admin", refund: "Returned stamps" } as Record<string, string>,
    grantLine: (left: number, units: number, until: string) => `${left} of ${units} left · until ${until}`,
    orderStatus: { open: "Started, not paid", completed: "Paid", expired: "Not paid in time", unknown: "Not confirmed by the provider" } as Record<string, string>,
    accountCard: "Plan and stamps",
    accountLine: (plan: string, stamps: number) => `${plan} · ${stamps} stamps left`,
    accountStorage: (used: string, quota: string) => `Storage ${used} of ${quota}`,
    manage: "Manage in Billing",
    badge: (n: number) => (n === 1 ? "1 stamp" : `${n} stamps`),
    badgeLabel: (n: number) => `Stamps left: ${n}. Open Billing.`,
    badgeNote: (messages: number, limit: number) => `Open Billing. Technical limit: ${messages} of ${limit} deliveries in the last 24 hours.`,
    serviceNote: "Service accounts have no plan or stamps. Their technical limit is shown in the header.",
    pricing: {
      title: "Plans and stamps",
      lead: "Plans and stamp packs, as our payment provider sells them.",
      stampDef: "One stamp is one delivery to one recipient. Your own copy is free. A capsule you sent stays readable by you and its recipients whatever happens to your plan.",
      signIn: "Sign in to see your plan",
      open: "Open Billing",
      pilotLead: "Vrelis is free during the pilot. Nothing is charged: there is no card to enter and nothing renews by itself.",
      pilotTitle: "What you get in the pilot",
      pilotPlans: "Every member has a plan: a number of stamps each month and storage for files. After you sign in, Billing shows your plan and what is left.",
      pilotChanges: "If paid plans come later, you will see the offer and its terms before you decide. Using the pilot does not commit you to any charge, and what you sent stays readable whatever happens to your plan.",
    },
  },
  adminBilling: {
    reasonNeeded: "Give a reason of 3 to 200 characters.",
    launchTitle: "Launch plan (Legacy)",
    launchLine: (count: number, cap: number) => `${count} of ${cap} members were given Legacy.`,
    launchOn: "New members get Legacy now.",
    launchOffSwitch: "The switch is off: new members get Free.",
    launchFull: "All seats are used: new members get Free.",
    launchClose: "Stop giving Legacy",
    launchOpen: "Give Legacy to new members",
    confirmLaunchClose: "Stop giving Legacy to new members? Members who have it keep it.",
    confirmLaunchOpen: "Give Legacy to new members again, while seats remain?",
    launchNote: "Opening registration does not give Legacy by itself. A seat that was used is never freed.",
    memberTitle: "Plan and stamps",
    memberShow: "Show plan and stamps",
    memberLoading: "Loading…",
    memberLine: (plan: string, stamps: number, used: string, quota: string) => `${plan} · ${stamps} stamps · storage ${used} of ${quota}`,
    rights: "Rights now and later",
    noRights: "No rights besides Free. Rights from a purchase or an admin grant are listed here with their dates.",
    why: (reason: string) => `reason: ${reason}`,
    endRight: "End now",
    confirmEnd: "End this right now? Stamps already given for the month stay.",
    addStamps: "Add stamps",
    stamps: "Stamps (1 to 10 000)",
    reason: "Reason (3 to 200 characters)",
    add: "Add",
    giveRight: "Give a plan",
    plan: "Plan",
    days: "Days (empty: no end)",
    give: "Give",
    addStorage: "Add storage",
    gib: "GiB (1 to 100)",
    done: "Done.",
    notYours: "Only the owner changes the billing of an admin or the owner.",
  },

  adminPayments: {
    tab: "Payments",
    providerTitle: "Payment provider",
    providerNames: { fake: "Test provider (no real payments)", paddle: "Paddle", dodo: "Dodo Payments" } as Record<string, string>,
    sandbox: "sandbox: test cards, no real money",
    live: "live: real payments",
    fakeNote: "The test provider sells nothing for real. Its events come only from local tests (smoke, E2E).",
    keysSet: "Keys are set.",
    keysMissing: (names: string) => `Missing: ${names}. Set them with wrangler secret put.`,
    secretNames: { api_key: "API key", webhook_secret: "webhook secret", client_token: "client token (checkout on the page)" } as Record<string, string>,
    webhookAt: "At the provider, send webhooks to",
    catalogueError: (detail: string) => `The provider's price list could not be read: ${detail}`,
    refresh: "Read again",
    pricesTitle: (n: number) => `Prices at the provider (${n})`,
    noPrices: "The provider sells nothing yet. Create products and prices in its dashboard, then read again.",
    oneOff: "one-off",
    notSold: "not sold on Vrelis (no mapping)",
    notListed: "paid for, but not in the provider's list now",
    map: "Map",
    edit: "Change",
    grantsPlan: (plan: string) => `grants ${plan} for each paid period`,
    inactive: "not sold now",
    otherMappings: "Mappings of prices the provider no longer lists",
    ownerOnly: "Only the owner changes what a price grants.",
    formLabel: (id: string) => `What ${id} grants`,
    kind: "Kind",
    kindSubscription: "Subscription (a plan each period)",
    kindPack: "Pack (one-off)",
    stamps: "Stamps",
    stampMonths: "Stamps valid (months)",
    storageMib: "Storage for good (MiB)",
    fileMib: "Files per capsule (MiB)",
    fileMonths: "Files right (months)",
    active: "Sold on Vrelis",
    save: "Save",
    eventsTitle: (n: number) => `Payments that granted nothing (${n})`,
    noEvents: "None. Every paid event granted its rights.",
    unmapped: "Price not mapped",
    unmatched: "Member not found",
    flagged: "Paid, needs action at the provider: see the note",
    invalid: "Signed by the provider but not understood",
    noMember: "no member",
    replay: "Apply again",
    stillWaiting: (detail: string) => `Still nothing to grant: ${detail}`,
    eventsNote: "Map the price first, then apply the event again. Rights are granted once, however often you try.",
    purchasesTitle: "Purchases",
    purchaseFilter: { completed: "Paid", open: "Started", all: "All" } as Record<string, string>,
    noPurchases: "No purchases. Every checkout a member opens at the provider is listed here with its state.",
    memberIds: "At the provider",
  },

  pending: {
    incomingTitle: "Waiting for you",
    from: (name: string) => `from ${name}`,
    askText: "Ask the sender for it. They confirm it is you, then it arrives in your Inbox.",
    needKey: "Set up your key first; the sender seals the capsule to it.",
    ask: "Ask for it",
    asked: "You asked; waiting for the sender to confirm.",
    declined: "The sender declined.",
    expired: "This has expired.",
    outgoingTitle: "Sent to people who were not members",
    to: (email: string) => `to ${email}`,
    waitingFor: (until: string) => `Waiting for them to join and ask (until ${until}).`,
    requestedBy: (name: string, email: string, fp: string) => `${name} (${email}, key ${fp}) asks for it. Approve only if this is the person you meant.`,
    approve: "Approve",
    decline: "Decline",
    cancel: "withdraw",
    confirmApprove: (name: string, email: string, fp: string) => `Seal this capsule to ${name} (${email}, key ${fp})?`,
    confirmDecline: (email: string) => `Withdraw this capsule for ${email}? Your copy stays in Sent.`,
    purpose: "to pass the capsule on",
  },

  developersPage: {
    title: "Vrelis API",
    lead: "Service accounts read and send time capsules through the API at /api/v1. Your service builds, encrypts and signs every capsule itself with the SDK; Vrelis stores ciphertext and checks signatures, follows, blocks and limits. It never encrypts or signs for you.",
    accessTitle: "Getting access",
    access: [
      "The admin sends a service invite to your service's address; signing in with it makes a service account. Other members see it as an ordinary member.",
      "Under Account, Keys, make the service's key and keep its 12 words in your service's secret store: they are the service's identity.",
      "Under Account, Developers, make an API token (shown once) and send it as Authorization: Bearer <token>.",
    ],
    securityTitle: "Security",
    security: [
      "Whoever has the 12 words signs as your service and reads its private capsules. Keep them out of code and logs.",
      "A token only reads and sends capsules. It cannot manage keys, tokens or sign-in methods, so a leaked server cannot take over the account.",
      "If a token leaks, revoke it under Account, Developers; your sign-in alone is enough. If the 12 words leak, use Reset key under Account, Keys.",
      "Private capsules go only to members the service follows and who do not block it; a group send leaves out whoever blocks it.",
    ],
    limitsTitle: "Limits",
    limits: [
      "50 deliveries in total per service account for now: each recipient's wrap counts, your own copies are free.",
      "Per token: 60 requests a minute, 10 000 a day, at most 100 rows per list page, 500 MiB of attachment downloads a day.",
      "Attachments: the same limits as in the web app (10 files, the total size the API reports in /me).",
    ],
    sdkTitle: "SDK",
    sdkText: "The SDK is the package @timecapsule/crypto from the Vrelis repository (TypeScript). sealPublic and sealPrivate build capsules, openPublic and openPrivate open them after their time, encryptFile and decryptFile handle attachments, sealAddressBook and openAddressBook the address book. A private capsule to one member:",
    referenceTitle: "Reference",
    referenceText: "Every route below comes from the OpenAPI 3.1 document the server builds from its own route definitions; open it in any OpenAPI tool:",
    tokenLabel: "API token for Try it (kept in this page only)",
    tokenNote: "Try it sends real requests with this token. Use a read-only token to look around.",
    parameter: "Parameter",
    where: "In",
    description: "Description",
    body: "Request body (JSON):",
    binaryBody: "Request body: the encrypted file bytes (application/octet-stream).",
    responses: "Responses:",
    tryIt: "Try it",
    send: "Send",
    binaryNoTry: "Upload from your service",
    schemas: "Schemas",
  },
  developers: {
    title: "Developers",
    note: "API tokens let your service read and send capsules through the Vrelis API. A token cannot manage keys, tokens or sign-in methods.",
    docsLink: "API documentation",
    tokenName: "Token name (for example the server it runs on)",
    readOnly: "Read only (no sending)",
    create: "Make a token",
    shownOnce: "Copy this token now and keep it in your service's secret store. It is shown only once; Vrelis keeps only a hash of it.",
    copied: "I copied it",
    tokensTitle: "API tokens",
    noTokens: "No tokens. A token lets a program of yours call the Vrelis API as this account, within the scopes and limits you give it.",
    readWrite: "read and send",
    readOnlyShort: "read only",
    revoked: "revoked",
    lastUsed: (d: string) => `last used ${d}`,
    neverUsed: "never used",
    revoke: "Revoke",
  },
  keyFlow: {
    title: "Your key",
    signature: (
      <>
        <b>Your key</b> is your main signature: it signs every capsule you send, and it is the key that opens private capsules sent to you. It is made in
        this browser; Vrelis never sees it.
      </>
    ),
    whoever: (
      <>
        Whoever has your 12 words <b>is</b> you: they can sign as you and read your private capsules.
      </>
    ),
    createNew: "Create my new key",
    skip: "Skip for now",
    skipNote: "Without a key you can read public capsules, but not write anything or receive private capsules. You can create it later under Account, Keys.",
    announce: "The next screen shows your 12 words. Before you open it:",
    announceChecks: [
      "make sure nobody can see your screen,",
      "nothing is recording or sharing it (screen sharing, a camera, a screenshot tool),",
      "have paper and a pen ready.",
    ],
    showPhrase: "Show my phrase",
  },
  keys: {
    title: "Keys",
    note: "Your key signs what you send and opens private capsules sent to you.",
    getTitle: "Get your key on this device",
    getText: "Your account has a key, but this device does not hold it. Use your passkey and PIN, or your recovery phrase.",
    getButton: "Get my key",
    activeTitle: "Your current key",
    fingerprint: "Fingerprint:",
    fullKey: "Full key",
    reset: "Reset key",
    resetNote: "If your phrase was stolen or you want a new one: the current key is revoked and you make a new one.",
    listTitle: "All keys of this account",
    state: { active: "active", retired: "earlier", revoked: "revoked" },
    created: (d: string) => `created ${d}`,
    lastUsed: (d: string) => `last used ${d}`,
    neverUsed: "never used",
    revokedAt: (d: string) => `revoked ${d}`,
    stolenSince: (d: string) => `, stolen since ${d}`,
    resetTitle: "Reset key",
    resetText: "Revoking stops your current key for good: nobody can send with it or to it any more. Then you make a new key with a new phrase, and that is you from now on.",
    typeCurrent: "Type the 12 words of your current key.",
    notCurrent: "This phrase is not your current key.",
    notStolen: "Nothing was stolen; I just want a new key.",
    wasStolen: "Someone may have my phrase or key.",
    stolenSinceLabel: "Since when (leave empty if you do not know):",
    stolenNote: "Capsules you sent from then on are marked \"revoked key\" for everyone who sees them. Without a date, everything since this key was made is marked.",
    keepOld: "Keep the old key in my keyring",
    keepOldNote: "On vrelis.com, capsules sent to the old key keep opening without the old phrase. In a downloaded archive and the offline opener they open only with the old phrase, so keep it too.",
    ownPhraseNote: "You keep the old phrase yourself; to open capsules sent to the old key you will add it under Keyring.",
    revokeButton: "Revoke my key",
    revokedNow: "Your old key is revoked. Now make your new key.",
    keyringTitle: "Keyring",
    keyringNote: "Your earlier keys, encrypted with your current key. Vrelis cannot open it.",
    keyringPurpose: "to open your keyring",
    showKeyring: "Show what it holds",
    keyringEmpty: "Empty. After Reset key your earlier keys are kept here, so capsules sent to them still open on vrelis.com.",
    keyringLocked: "Your keyring is still sealed to an earlier key: a key reset stopped before it moved. Add that key's phrase below; the keyring moves to your current key with everything it holds.",
    keyringLockedReset: "Your new key is ready, but the keyring could not be opened with the old or the new key, so it was left unchanged. Add the phrase of the key it is sealed to under Keyring.",
    retryKeyring: "Try moving the keyring and the address book again",
    later: "Later",
    keyringFull: "Your keyring is full, so the old key cannot go in. Keep the old phrase yourself (untick the box) and try again.",
    addOld: "Add an earlier phrase",
    notOldKey: "This phrase is not an earlier key of this account.",
    addOldButton: "Add to keyring",
  },
  reauth: {
    title: "Confirm it is you",
    text: (purpose: string) => `Sign in again ${purpose}. Only your own sign-in counts.`,
    purposes: { createKey: "to set up this key", revokeKey: "to revoke your key", createToken: "to make an API token" },
    password: "Password",
    withPassword: "Confirm with password",
    withPasskey: "Confirm with a passkey",
    withGoogle: "Confirm with Google",
    popupBlocked: "The browser blocked the Google window. Allow pop-ups for this site and try again.",
    mismatch: "That sign-in belongs to another account.",
    closeWindow: "Done. You can close this window.",
    needStrong: "This needs your password or a passkey: Google cannot confirm that it is you at this computer. Add a password under Account, “How you sign in” (you get a mail link), then try again.",
    openSignIns: "Open “How you sign in”",
  },
  connect: {
    title: "Request to connect",
    request: (name: string) => `${name} wants to connect with you on Vrelis.`,
    note: "If you accept, you follow each other, so each of you can send the other private capsules. If you decline, they only see that their invite expired.",
    accept: "Accept",
    decline: "Decline",
    accepted: (name: string) => `You and ${name} now follow each other.`,
    declined: "Declined.",
    gone: "This request is no longer open.",
  },
  people: {
    tabs: { following: "Following", followers: "Followers", blocked: "Blocked", hidden: "Hidden" },
    none: {
      following: "You follow nobody yet. Follow someone from their name on a capsule or from search: then you can send them private capsules and see their public ones under Following.",
      followers: "Nobody follows you yet. A member who follows you can send you private capsules.",
      blocked: "You block nobody. Block a member from the menu on their name if you want no private capsules from them.",
      hidden: "You hide nobody. Hide a member from the menu on their name to take their public capsules out of your Feed.",
    },
    notes: {
      following: "Private capsules go only to members you follow.",
      followers: "Only you see this list.",
      blocked: "A blocked member cannot put private capsules into your Inbox; you can still follow them and read their public capsules. Their earlier capsules are under Inbox, Blocked.",
      hidden: "A hidden member's public capsules leave your Feed on every device. Nothing changes for them, and you can still write to them.",
    },
    followBack: (name: string) => `Follow ${name}`,
    since: (date: string) => `since ${date}`,
  },
  contacts: {
    title: "Address book",
    lead: "Aliases for the people you follow and your own groups (family, friends, colleagues…). One person can be in many groups. The whole book is encrypted with your key in this browser; we only store it.",
    open: "Open the address book",
    purposeOpen: "to open your address book",
    purposeSave: "to save your address book",
    unreadable: "Your address book was saved with an earlier key that is not in your keyring, so none of your keys can open it. Nothing was changed.",
    addOldPhrase: "Add that key's phrase under Keys -> Keyring: the address book then opens and moves to your current key.",
    startOver: "Start a new, empty address book",
    startOverConfirm: "Replace the stored address book with an empty one? Its groups and names are lost for good, even if you find the old phrase later.",
    groups: "Groups",
    noGroups: "No groups yet. A group is a named list of people you follow; choose it in Write to send one capsule to all of them.",
    groupName: "Group name",
    memberCount: (n: number) => `${n} ${n === 1 ? "person" : "people"}`,
    deleteGroup: "delete",
    confirmDelete: (name: string) => `Delete the group "${name}"? The people stay followed.`,
    newGroup: "New group",
    newGroupHint: "e.g. Family",
    addGroup: "Add group",
    people: "People you follow",
    nobody: "You follow nobody yet.",
    toAccount: "Account",
    alias: "Alias (only you see it)",
    aliasHint: "e.g. the director",
    save: "Save",
    saved: "Saved",
    changedElsewhere: "The address book was changed on another device. Open it again; your unsaved changes here are lost.",
  },

  admin: {
    registration: {
      title: "Registration",
      isOpen: "Open: anyone can register, without an invite.",
      isClosed: "Closed: only with an invite (the pilot default).",
      open: "Open registration",
      close: "Close registration",
      confirmOpen: "Let anyone register without an invite?",
      confirmClose: "Allow registration only with an invite again?",
    },
    title: "Admin",
    newInvite: "New invite",
    email: "Email of the person you invite",
    emailNote: "Only this Google account can use the invite. The link alone is not enough.",
    service: "Service account (uses the API with tokens; the address must not have an account yet)",
    serviceNote: "Other members see a service account as an ordinary member; only you and the account see that it is one.",
    anyone: "Instead, anyone with the link may join (the first one who uses it)",
    days: "Valid for days",
    create: "Create invite",
    sendLink: "Send this link: ",
    invites: "Invites",
    columns: { code: "Code", for: "For", status: "Status", expires: "Expires", name: "Name", email: "Email", joined: "Joined", key: "Key", serverCopy: "Server copy" },
    anyoneWithLink: "anyone with the link",
    usedBy: (name: string) => `used by ${name}`,
    expired: "expired",
    claimed: "claimed: a login started with it",
    open: "open",
    revoke: "Revoke",
    confirmRevoke: (code: string, email: string | null) => `Revoke the invite ${code}${email ? ` for ${email}` : ""}? The link stops working.`,
    members: (n: number) => `Members (${n})`,
    states: { all: "Everyone", predefined:"Predefined", active: "Active", suspended:"Suspended", deactivated: "Deactivated" },
    membersNote:
      "A member is set up in three steps: logged in (joined), key registered, passkey and PIN protecting the key (server copy). Without a passkey that can derive keys there is no server copy; the key then lives only on that device and in the recovery phrase.",
    deactivatedOn: (time: string) => ` · deactivated ${time}`,
    since: (time: string) => ` since ${time}`,
    notSetUp: "not set up",
    yes: "yes",
    none: "none",
    reset: "Reset identity",
    reactivate: "Reactivate",
    deactivate: "Deactivate",
    confirmReset: (name: string) =>
      `Reset the identity of ${name}?\n\nOnly do this after confirming with ${name} outside the app (phone, in person) that they lost their recovery phrase and every device. Their current key is retired and their sessions end; they then create a new identity. Private capsules sent to the old key cannot be opened with the new one.`,
    askReason: (name: string) => `Why is the identity of ${name} reset? (3 to 200 characters, kept in the log)`,
    reasonNeeded: "The reset needs a reason of 3 to 200 characters.",
    makeAdmin: "Make admin",
    removeAdmin: "Remove admin",
    confirmMakeAdmin: (name: string, email: string) =>
      `Make ${name} (${email}) an admin?\n\nAn admin sees every member's email address and every invite, makes invites and links anyone can use, opens registration, deactivates members and resets their identity. An admin cannot do any of this to you or to another admin. You can remove the role at any time.`,
    confirmRemoveAdmin: (name: string) => `Remove the admin role from ${name}? They become an ordinary member at their next click.`,
    askRoleReason: (name: string) => `Why does the role of ${name} change? (3 to 200 characters, kept in the log)`,
    roleReasonNeeded: "A role change needs a reason of 3 to 200 characters.",
    confirmReactivate: (name: string, email: string) => `Reactivate ${name}? They can log in again with ${email}.`,
    confirmDeactivate: (name: string, email: string) =>
      `Deactivate ${name} (${email})?\n\nThey are logged out everywhere at once, cannot log in again, disappear from the member list and cannot receive private capsules. Capsules they already sent stay. You can reactivate them later.`,
  },

  attachments: {
    noImages: "this browser cannot process images",
    tooBig: (name: string, size: string, left: string) => `${name} is ${size}; ${left} is left for this capsule`,
    notFound: "attachment not found",
  },

  /** Errors with a stable code from the API (4b step 3b); unknown codes show the server's English detail. */
  apiErrors: {
    unsubscribe_invalid: "This link is not valid. You can change notifications in Account.",
    invalid_request: (detail: string) => `The request was refused: ${detail}`,
    login_required: "Your session has ended. Log in again.",
    forbidden: "You are not allowed to do this.",
    not_found: "Not found.",
    conflict: (detail: string) => `This conflicts with the current state: ${detail}`,
    too_large: "That is too large.",
    rate_limited: "Too many requests. Try again later.",
    unavailable: "This service is not available right now. Try again later.",
    internal: "Something went wrong on the server. Try again; if it repeats, report the problem.",
    daily_limit: "You reached today's limit of 100 deliveries. Try again tomorrow.",
    upload_quota: "You reached the upload limit: 20 unused files or 50 MiB in 24 hours.",
    blob_too_large: "These files are larger than your plan allows for one capsule. See Billing.",
    attachment_unavailable: "An attachment is missing, expired or already used. Remove it and add it again.",
    profile_outdated: "A newer profile change is already saved. Reload the page.",
    server_copy_locked: "The copy on our server is blocked after 10 wrong PINs. Use your recovery phrase.",
    identity_changed: "Your identity changed meanwhile. Reload the page and try again.",
    current_key_required: "Changing your identity needs your current key.",
    key_owned: "This key already belongs to another member.",
    member_suspended: "Your account is suspended. Contact the administrator at support@vrelis.com.",
    status_conflict: "The user changed meanwhile. Reload and try again.",
    owner_only: "Only the owner can change these settings.",
    target_self: "You cannot block your own account.",
    pilot_full: "All pilot places are occupied. Contact the administrator.",
    member_deactivated: "This member was deactivated by the admin.",
    invite_required: "This pilot requires an approved email address or an invitation. Open the invite link you received, then register.",
    invite_invalid: "This invite is not valid: it was already used, has expired, or is for a different email address.",
    invalid_email: "Enter a valid email address.",
    invalid_credentials: "Wrong email or password.",
    login_locked: "Too many wrong passwords. Wait 15 minutes or reset the password.",
    mail_limit: "Too many mails to this address today. Try again tomorrow.",
    mail_unavailable: "The mail could not be sent. Try again later.",
    link_expired: "This link has expired or was already used. Ask for a new one.",
    password_exists: "This account already has a password.",
    last_login: "This is your last sign-in method; add another one first.",
    passkey_refused: "This passkey cannot sign in here. Use it after you set up your key on this device, or sign in another way.",
    passkey_exists: "This passkey already signs in.",
    passkey_unsupported: "This passkey type cannot be used for sign-in.",
    pending_limit: "You have 10 capsules waiting for new members.",
    pending_closed: "This capsule can no longer be asked for or approved.",
    key_required: "Set up your key first.",
    subject_required: "A capsule needs a subject.",
    recipient_not_followed: "Private capsules go only to members you follow. Follow them first.",
    recipient_blocked: "This member does not accept your private capsules.",
    reauth_required: "Sign in again to confirm it is you.",
    reauth_mismatch: "That sign-in belongs to another account.",
    block_limit: "You can block at most 1000 members.",
    hide_limit: "You can hide at most 1000 members.",
    invite_limit: "Your configured open invitation limit is reached. Revoke one or wait until one is used.",
    follow_limit: "You follow the maximum of 500 members.",
    address_book_changed: "The address book was changed on another device. Open it again.",
    archive_refused: "Only a capsule you can see, whose opening time has passed, can be archived.",
    reply_unknown: "The capsule you reply to has no reply envelope, or does not exist.",
    reply_not_recipient: "Only the member who received the request can reply to it.",
    reply_recipient: "A reply goes to the active key of the member who asked for it.",
    reply_unlock_mismatch: "The reply must open at the time the envelope names.",
    envelope_expired: "The time the sender set has passed; no reply is accepted any more.",
    target_owner: "Nobody can do this to the owner.",
    target_admin: "Only the owner can do this to an admin.",
    role_unchanged: "This member already has that role. Reload the list.",
    no_stamps: "You have no stamps left for this. See Billing.",
    storage_quota: "Your storage is full. Free some space or choose a plan with more in Billing.",
    submission_conflict: "These events are already stored differently. Reload the page and write the capsule again.",
    retail_only: "Service accounts have no plan or stamps.",
    idempotency_conflict: "That request key was used for something else. Try again.",
    idempotency_key_invalid: "That request key is not valid. Reload the page and try again.",
    pack_limit: "This account already holds the most extra storage packs it can.",
    checkout_open: "A payment for another plan is still open. Finish it, or try again when it lapses.",
    price_unavailable: "This price is not sold here.",
    subscription_exists: "You already have a subscription. Change or cancel it in the billing portal.",
    selling_off: "Selling is not switched on yet: nothing can be bought.",
    mapping_invalid: (detail: string) => `The price mapping is not valid: ${detail}`,
    billing_unavailable: "Payments are not available right now. Try again later.",
    portal_unavailable: "There is nothing to manage at the payment provider yet.",
    event_not_waiting: "This event no longer waits: it was applied already.",
    entitlement_not_manual: "Only a right given by hand can be ended here.",
    billing_account_not_empty: "This login already has billing rights and cannot take over an identity.",
    units_invalid: "Stamps must be a whole number from 1 to 10 000.",
    reason_invalid: "Give a reason of 3 to 200 characters.",
    expiry_invalid: "The end time must be in the future.",
    bytes_invalid: "Storage must be between 1 byte and 100 GiB.",
    plan_unknown: "Unknown plan.",
    upload_expired: "The upload expired. Add the file again.",
    attachment_pending: "A file is still uploading. Try again in a moment.",
    billing_invariant: "A billing check failed on the server. Nothing was sent or charged. Report the problem.",
    history_kind_invalid: "Unknown list.",
    payment_required: "This needs something your account does not have. See Billing.",
  } as Record<string, string | ((detail: string) => string)>,

  errors: {
    notYet: "Not yet. The time beacon for this capsule has not been published; it can lag a few seconds. Try again shortly.",
    beacon: "Could not reach the drand time beacon. Check your connection and try again.",
    notRecipient: "This capsule is addressed to someone else.",
    signature: "The signature does not match. This capsule may have been tampered with.",
    damaged: (detail: string) => `This capsule is damaged: ${detail}`,
    keyNotUnlocked: "Your key is not unlocked on this device.",
    keyMismatch: "This device holds another identity than your account's current one. Set up your key on this device first.",
    keyMissing: "Your key is not on this device. Set it up first: the link at the top of the page.",
    pinBusy: "Another action is already waiting for your PIN.",
    wrongPinBlocked: "Wrong PIN. The server copy is now blocked; use your recovery phrase.",
    wrongPin: (left: number | null) => `Wrong PIN${left === null ? "" : `. ${left} attempt${left === 1 ? "" : "s"} left before the server copy is blocked`}.`,
    passkeyClosed: "The passkey prompt was closed or timed out.",
    passkeyFailed: (detail: string) => `Passkey error: ${detail}`,
    passkeyUnsupported: "This browser cannot create passkeys.",
    passkeyUnusable: "This browser cannot use passkeys.",
    noPasskeyCreated: "No passkey was created.",
    noPasskeyUsed: "No passkey was used.",
    noPrf: "This passkey cannot derive keys (no PRF support).",
    lockedDuringDownload: "Your key was locked while the download was being prepared, so nothing was saved. Try again.",
    nothingToDownload: "There is nothing here you could open later: only the recipient can read this capsule.",
    noneDownloadable: "None of the selected capsules can be opened by you later.",
    exportTooMany: (max: number) => `One archive holds at most ${max} capsules. Select fewer and download the rest in another archive.`,
    exportTooBig: "These capsules and their files are too large for one archive. Select fewer and download the rest in another archive.",
  },

  /** README.txt of a downloaded archive, in the member's language. */
  archiveReadme: (count: number, date: string, includeOpened: boolean, withOpener: boolean): string[] => [
    "Vrelis time capsules",
    "====================",
    "",
    `This archive holds ${count} capsule(s), downloaded ${date}.`,
    "",
    "capsules/<id>.json   each capsule as it was signed: a public capsule (Nostr kind 1041), or a",
    "                     private capsule addressed to you (kind 1059). After the unlock time the",
    "                     file also holds the drand beacon of its round, so it no longer needs drand.",
    "blobs/<sha256>.bin   attached files, still encrypted; the keys are inside the time-locked capsule.",
    includeOpened ? "opened/<id>/...      opened text is inside the .json; opened files are here. NOT ENCRYPTED." : "",
    withOpener ? "vrelis-opener.html   offline opener: open it in a browser and choose this .zip file." : "",
    "",
    "To open: open vrelis-opener.html in Chrome, Firefox or Safari, choose this .zip. Private capsules",
    "need your 12-word recovery phrase. The opener never contacts vrelis.com; for a capsule without a",
    "stored beacon it asks the public drand relays (api.drand.sh and others) for the beacon.",
    "",
    "If you ever changed your key: a private capsule opens only with the phrase of the key it was sent",
    "to. The opener has no keyring, so keep every old phrase next to this archive.",
    "",
    "Everything here uses public standards with open-source code: Nostr events (NIP-01, NIP-44, NIP-59),",
    "drand timelock encryption (tlock, age v1 format), BIP-39 recovery phrases (NIP-06).",
    "",
    includeOpened ? "Opened text and files in this archive are plain on your disk: anyone with the file can read them." : "",
  ],
};
